import { PayrollCloseArtifacts } from "./PayrollCloseArtifacts.js";
import { PayrollPohodaVersions } from "./PayrollPohodaVersions.js";
import { PayrollBalanceLedger } from "./PayrollBalanceLedger.js";
import { PayrollPrepareOverview } from "./PayrollPrepareOverview.js";
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "../lib/navigation.js";
import { periodCoversMonth } from "@payday/contracts";
import type {
  OpenProjectBalanceState,
  OpenProjectPohodaPreview,
} from "../types/openproject.js";
import type {
  AdminUser,
  AuditEvent,
  BalanceRun,
  CalculationReadiness,
  CalculationReadinessMissing,
  ConfirmationState,
  CrossCheck,
  EmployeeStatusFilter,
  PayslipBatch,
  PayslipListFilter,
  PayslipRecord,
  PayslipResolveDraft,
  PayslipSubstep,
  PayslipUploadFileResult,
  PohodaExportVersionSummary,
  TimesheetVersionState,
  YearSummaryEmployee,
  YearSummaryMonth,
} from "../types/payrollAdmin.js";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleDotDashed,
  ClipboardCheck,
  Eye,
  FileSpreadsheet,
  FileText,
  LockKeyhole,
  RotateCcw,
  Search,
  ShieldCheck,
  Upload,
} from "lucide-react";

import { useAuth } from "../app/auth.js";
import { api as controlApi } from "../../api.js";
import { ScopedRefreshControl } from "../components/payroll/ScopedRefreshControl.js";
import { refreshScopedSourceQueries } from "../lib/scopedRefresh.js";
import { Panel } from "../components/Panel.js";
import { PayrollWorkflowRail } from "../components/payroll/PayrollWorkflowRail.js";
import { PayrollAuditLinks } from "../components/payroll/PayrollAuditLinks.js";
import { PayslipSheet } from "../components/payroll/PayslipSheet.js";
import {
  ValueCorrectionInspector,
  type ValueCorrectionTarget,
} from "../components/payroll/ValueCorrectionInspector.js";
import type { ValueCorrectionFieldKey } from "../components/payroll/valueCorrectionFields.js";
import {
  BalanceResolutionModal,
  type BalanceCheckKey,
} from "../components/payroll/BalanceResolutionModal.js";
import {
  Button,
  ActionFeedback,
  Badge,
  EmployeeIdentity,
  Modal,
  StatusChip,
} from "../components/ui/index.js";
import {
  WorkloadBadge,
} from "../components/payroll/Badges.js";
import {
  MonthYearPicker,
  PayrollUnitToggle,
  monthLabel,
  shortMonthLabel,
} from "../components/payroll/PeriodPickers.js";
import {
  PayslipDropZone,
  PohodaWorkbookPreviewTable,
  payslipIssueContext,
  payslipReviewCategoryForStatus,
  payrollLoadErrorMessage,
} from "../components/payroll/PayslipPresentational.js";
import type { PayslipReviewCategory } from "../components/payroll/PayslipPresentational.js";
import { PayrollEmailMenu } from "../components/payroll/PayrollEmailMenu.js";
import {
  OvertimePotDialog,
} from "../components/payroll/OvertimePotDialog.js";
import { ConfirmDialog } from "../components/payroll/ConfirmDialog.js";
import {
  ComparisonHeader,
  ComparisonMetricCell,
  MetricCell,
  OvertimePotMetricCell,
  PeriodComparisonMetricCell,
  PeriodHoursMetricCell,
  ValueCorrectionComparisonMetricCell,
  ValueCorrectionMetricCell,
  hasPaidLeaveEvidence,
} from "../components/payroll/MetricCells.js";
import {
  PeriodDetailTotals,
  PeriodSummaryMobileCard,
  hasCalculationValues,
  hasPeriodSummaryValues,
  yearDetailTotals,
} from "../components/payroll/PeriodSummary.js";
import {
  EmployeeAuditTimelineItem,
  auditValue,
} from "../components/payroll/AuditTimeline.js";
import {
  api,
  downloadFile,
  PayrollApiError,
  type PayrollAuditEvent as ApiPayrollAuditEvent,
} from "../lib/api.js";
import { queryClient } from "../lib/queryClient.js";
import { parseWorkbookPreview } from "../lib/xlsxPreview.js";
import {
  buildApprovalBlockingLog,
} from "./payrollApprovalIssues.js";

import {
  payslipNeedsAttention,
  payslipRequiresReplacement,
  payslipUploadResultMessage,
} from "./payrollInputIntegrity.js";
import {
  formatDuration,
  mismatchTooltip,
} from "./payrollDisplay.js";
import type { PayrollDisplayUnit } from "./payrollDisplay.js";
import {
  formatBulkLockFeedback,
  summarizeTimesheetLocks,
} from "./payrollLockFeedback.js";
import {
  applyAuthoritativePayrollWorkflow,
  authoritativePayrollUsersForMonth,
  completePrepareStageAfterTimesheetLock,
  createInitialBalanceCalculationGuard,
  derivePayrollWorkflow,
  guardPayrollWorkflowAgainstPayslipMismatch,
  hasAuthoritativePayslipProgress,
  payslipDiscardConfirmationDescription,
  payslipDiscardRequiresReplacement,
  payslipDuplicateDisposition,
  payrollNextDisabledReason,
  payrollNextLabel,
  recommendedPayrollStage,
  refreshPayrollWorkflowQueries,
  resolveActivePayrollWorkflowStageId,
  resolveAuthoritativePayslipProgress,
  selectLatestPayslipActionBatch,
  shouldResetPayrollWorkflowSelection,
  type AuthoritativePayrollWorkflowStage,
  type PayrollWorkflowStage,
  type PayrollWorkflowStageId,
} from "./payrollWorkflow.js";

const initialBalanceCalculationGuard = createInitialBalanceCalculationGuard();

const normalizeAuditEvents = (
  events: ApiPayrollAuditEvent[] | undefined,
): AuditEvent[] =>
  (events ?? []).map(
    (event) =>
      ({
        ...event,
        targetType: event.targetType ?? "",
        targetIdentifier: event.targetIdentifier ?? "",
        beforePayload: event.beforePayload ?? {},
        afterPayload: event.afterPayload ?? {},
      }) as unknown as AuditEvent,
  );

const currentLocalYYYYMM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const PAYROLL_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const PERIOD_START_MONTH = "2024-01";
const AUDIT_REASON_MIN_LENGTH = 15;
const AUDIT_REASON_MAX_LENGTH = 500;
const PAYROLL_CONTEXT_STORAGE_KEY = "payroll-context";

const payrollContextStorageKey = (principalId: string) =>
  `${PAYROLL_CONTEXT_STORAGE_KEY}:${principalId}`;

const readPayrollSearchParams = (principalId = "") => {
  if (typeof window === "undefined") return new URLSearchParams();
  const params = new URLSearchParams(window.location.search);
  const recovery = window.history.state?.payrollProfileRecovery;
  if (recovery && PAYROLL_MONTH_PATTERN.test(recovery.month ?? "")) {
    params.set("month", recovery.month);
    if (PAYROLL_MONTH_PATTERN.test(recovery.periodThrough ?? "")) params.set("periodThrough", recovery.periodThrough);
    if (recovery.resolve === "missing-payslip") params.set("stage", "payslips");
    return params;
  }
  if (
    window.history.state?.payrollContextPrincipalId &&
    window.history.state.payrollContextPrincipalId !== principalId
  )
    return new URLSearchParams();
  if (params.size) return params;
  if (!principalId) return params;
  try {
    const stored = new URLSearchParams(
      window.sessionStorage.getItem(payrollContextStorageKey(principalId)) ??
        "",
    );
    if (stored.get("principal") !== principalId) return params;
    stored.delete("principal");
    return stored;
  } catch {
    return params;
  }
};

const periodSummaryContextStorageKey = (principalId: string) => `period-summary-context:${principalId}`;
const readPeriodSummaryParams = (principalId: string) => {
  const params = new URLSearchParams(window.location.search);
  if (params.has("from") || params.has("through")) return params;
  try { return new URLSearchParams(window.sessionStorage.getItem(periodSummaryContextStorageKey(principalId)) ?? ""); }
  catch { return new URLSearchParams(); }
};

const validMonthOr = (value: string | null, fallback: string) =>
  value && PAYROLL_MONTH_PATTERN.test(value) ? value : fallback;

const validPeriodThroughOr = (value: string | null, fallback: string) => {
  const month = validMonthOr(value, fallback);
  return month >= PERIOD_START_MONTH ? month : fallback;
};

const validWorkflowStageOrNull = (
  value: string | null,
): PayrollWorkflowStageId | null =>
  value &&
  ["prepare", "pohoda", "payslips", "reconcile", "close"].includes(value)
    ? (value as PayrollWorkflowStageId)
    : null;

const validPayslipSubstep = (value: string | null): PayslipSubstep =>
  value && ["upload", "confirm"].includes(value)
    ? (value as PayslipSubstep)
    : "upload";

const PAYROLL_DISPLAY_UNIT_STORAGE_KEY = "payroll-display-unit";

const storedPayrollDisplayUnit = (): PayrollDisplayUnit => {
  if (typeof window === "undefined") return "hours";
  try {
    return window.localStorage.getItem(PAYROLL_DISPLAY_UNIT_STORAGE_KEY) ===
      "days"
      ? "days"
      : "hours";
  } catch {
    return "hours";
  }
};

const payrollHoursPerDayForMonth = (user: AdminUser, month: string) => {
  // Shape pre-filter before the shared helper: `periodCoversMonth` assumes
  // validated, well-formed YYYY-MM / YYYY-MM-DD inputs, so skip malformed
  // period boundaries before calling it.
  const periodDatePattern = /^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$/;
  const effectivePeriod = (user.profilePeriods ?? [])
    .filter(
      (period) =>
        periodDatePattern.test(period.validFromMonth) &&
        (!period.validToMonth ||
          periodDatePattern.test(period.validToMonth)) &&
        periodCoversMonth(
          period.validFromMonth,
          period.validToMonth ?? "",
          month,
        ),
    )
    .sort(
      (left, right) =>
        right.validFromMonth.localeCompare(left.validFromMonth) ||
        right.id - left.id,
    )[0];
  const hoursPerDay =
    effectivePeriod?.hoursPerDay ?? user.payrollHoursPerDay ?? 8;
  return Number.isFinite(hoursPerDay) && hoursPerDay > 0 ? hoursPerDay : 8;
};

const trimmedAuditReason = (value: string) => value.trim();
const auditReasonLength = (value: string) => trimmedAuditReason(value).length;
const auditReasonIsValid = (value: string) => {
  const length = auditReasonLength(value);
  return length >= AUDIT_REASON_MIN_LENGTH && length <= AUDIT_REASON_MAX_LENGTH;
};



const calculationMissingLabel: Record<CalculationReadinessMissing, string> = {
  no_eligible_employees: "No eligible employees",
  finalized_timesheet: "Finalized timesheet",
  usable_payslip: "Usable payslip",
  delivery_safe_payslip: "Verified single-person PDF",
  confirmed_payslip: "Confirmed payslip",
};



const payslipReviewCategoryMeta: Record<
  PayslipReviewCategory,
  { title: string; description: string }
> = {
  attention: {
    title: "Needs attention",
    description: "Review individually · excluded from bulk confirmation",
  },
  ready: {
    title: "Ready for confirmation",
    description: "Confirm all or review individually",
  },
  complete: {
    title: "Completed",
    description: "Already confirmed or safely removed",
  },
};

type PayrollPageView = "monthly" | "period-summary";

/**
 * Runtime shape of the payroll-workflow endpoint, which the api client types
 * as `Json`. Declared locally against actual usage so the workflow data can be
 * read without index-signature access.
 */
type PayrollWorkflowData = {
  pohodaOutdated?: boolean;
  version?: unknown;
  stages?: AuthoritativePayrollWorkflowStage[];
  payslipProgress?: unknown;
  closure?: { status?: unknown };
};

/** One published month-change-log row (api client types the endpoint as `Json`). */
type MonthChangeLogEntry = {
  id: number;
  versionNumber: number;
  fileName: string;
  createdByName: string;
  createdAt: string;
  manualChangeCount: number;
};

/** Upload summary actually consumed by the UI (richer than the endpoint's Json). */
type PayslipUploadSummary = {
  files: PayslipUploadFileResult[];
  acceptedCount: number;
  reviewCount: number;
  rejectedCount: number;
  skippedCount: number;
};

export const PayrollAdminPage = ({
  view = "monthly",
}: { view?: PayrollPageView } = {}) => {
  const periodSummaryOnly = view === "period-summary";
  const authQuery = useAuth();
  const payrollPrincipalId = authQuery.data?.user?.id ?? "";
  const payrollAccessQuery = useQuery({
    queryKey: ["openproject", "payroll-access"],
    queryFn: api.getOpenProjectPayrollAccess,
    enabled: Boolean(authQuery.data?.user),
    retry: false,
  });
  const hasPayrollAccess = Boolean(payrollAccessQuery.data?.officeAccess);
  const sourceCapabilityQuery = useQuery({
    queryKey: ["openproject", "source-capability"],
    queryFn: async () => {
      const availability = await controlApi.openProjectAvailability();
      if (!availability.available) return false;
      return (await controlApi.openProject())["mode"] === "enabled";
    },
    enabled: hasPayrollAccess && !periodSummaryOnly,
    retry: false,
    staleTime: 0,
  });
  const sourceEnabled = sourceCapabilityQuery.isSuccess && sourceCapabilityQuery.data === true;
  const [month, setMonth] = useState(() => {
    return validMonthOr(
      readPayrollSearchParams(payrollPrincipalId).get("month"),
      currentLocalYYYYMM(),
    );
  });
  const previousWorkflowMonthRef = useRef(month);
  const currentMonthRef = useRef(month);
  currentMonthRef.current = month;
  const periodFromMonth = PERIOD_START_MONTH;
  const [periodThroughMonth, setPeriodThroughMonth] = useState(() =>
    validPeriodThroughOr(
      periodSummaryOnly ? readPeriodSummaryParams(payrollPrincipalId).get("through") : readPayrollSearchParams(payrollPrincipalId).get("periodThrough"),
      currentLocalYYYYMM(),
    ),
  );
  const [reportFromMonth, setReportFromMonth] = useState(() => validPeriodThroughOr(periodSummaryOnly ? readPeriodSummaryParams(payrollPrincipalId).get("from") : null, `${periodThroughMonth.slice(0, 4)}-01`));
  const [displayUnit, setDisplayUnit] = useState<PayrollDisplayUnit>(
    storedPayrollDisplayUnit,
  );
  const [selectedWorkflowStage, setSelectedWorkflowStage] =
    useState<PayrollWorkflowStageId | null>(() =>
      validWorkflowStageOrNull(
        readPayrollSearchParams(payrollPrincipalId).get("stage"),
      ),
    );
  const [payslipSubstep, setPayslipSubstep] = useState<PayslipSubstep>(() =>
    validPayslipSubstep(
      readPayrollSearchParams(payrollPrincipalId).get("payslipStep"),
    ),
  );
  const [payslipListFilter, setPayslipListFilter] =
    useState<PayslipListFilter>("all");
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null,
  );
  const [reopenStage, setReopenStage] = useState<PayrollWorkflowStageId | null>(
    null,
  );
  const [reopenReason, setReopenReason] = useState("");
  const [payslipQueueIndex, setPayslipQueueIndex] = useState(0);
  const [balanceResolutionEmployeeId, setBalanceResolutionEmployeeId] =
    useState<number | null>(null);
  const [balanceResolutionCheckKey, setBalanceResolutionCheckKey] =
    useState<BalanceCheckKey | null>(null);
  const balanceResolutionTriggerRef = useRef<HTMLButtonElement | null>(null);
  const reopenTriggerRef = useRef<HTMLButtonElement | null>(null);
  const closeReopenDialog = () => {
    setReopenStage(null);
    setReopenReason("");
    window.setTimeout(() => {
      if (document.activeElement === document.body)
        reopenTriggerRef.current?.focus();
    }, 300);
  };
  const [feedback, setFeedback] = useState<{
    tone: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [workflowRefreshCount, setWorkflowRefreshCount] = useState(0);
  const [balanceCalculationFailure, setBalanceCalculationFailure] = useState<
    string | null
  >(() => initialBalanceCalculationGuard.state(month).error);
  const [pendingWorkflowFocus, setPendingWorkflowFocus] = useState(false);
  const [, setReplacementEmployeeName] = useState("");
  const replacementFileInputRef = useRef<HTMLInputElement>(null);
  const [payslipFiles, setPayslipFiles] = useState<File[]>([]);
  const payslipUploadPendingRef = useRef(false);
  const [payslipDropActive, setPayslipDropActive] = useState(false);
  const [payslipSheet, setPayslipSheet] = useState<{
    recordId: number;
    mode: "edit" | "read";
  } | null>(null);
  const [pohodaWorkbookPreviewId, setPohodaWorkbookPreviewId] = useState<
    number | null
  >(null);
  const [monthChangeLogPreviewId, setMonthChangeLogPreviewId] = useState<
    number | null
  >(null);
  const [monthChangeLogPreviewUrl, setMonthChangeLogPreviewUrl] = useState<
    string | null
  >(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilter>("all");
  const [hideZeroValueEmployees, setHideZeroValueEmployees] = useState(true);
  const [selectedYearEmployee, setSelectedYearEmployee] =
    useState<YearSummaryEmployee | null>(null);
  const [showEmptyPeriodMonths, setShowEmptyPeriodMonths] = useState(false);
  const [employeeAuditOpen, setEmployeeAuditOpen] = useState(false);
  const [payrollAuditOverlayOpen, setPayrollAuditOverlayOpen] = useState(() =>
    Boolean(readPayrollSearchParams().get("auditOverlay")),
  );
  const [valueCorrectionTarget, setValueCorrectionTarget] =
    useState<ValueCorrectionTarget | null>(null);
  const [, setCalculationDetailsOpen] = useState(false);
  const [prepareStageTarget, setPrepareStageTarget] =
    useState<HTMLDivElement | null>(null);
  const [calculationStageTarget, setCalculationStageTarget] =
    useState<HTMLDivElement | null>(null);
  const [hideZeroValueCalculationRows, setHideZeroValueCalculationRows] =
    useState(true);
  const [payslipResolveDrafts, setPayslipResolveDrafts] = useState<
    Record<number, PayslipResolveDraft>
  >({});
  const addPayslipFiles = (incomingFiles: File[]) => {
    if (payslipUploadPendingRef.current) return;
    const pdfFiles = incomingFiles.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf"),
    );
    const ignoredCount = incomingFiles.length - pdfFiles.length;
    setReplacementEmployeeName("");
    setPayslipFiles((currentFiles) => {
      const uniqueFiles = new Map(
        [...currentFiles, ...pdfFiles].map((file) => [
          `${file.name}:${file.size}:${file.lastModified}`,
          file,
        ]),
      );
      return Array.from(uniqueFiles.values());
    });
    if (pdfFiles.length === 0) {
      setFeedback({
        tone: "error",
        message:
          "Choose PDF payslip files. Other file types cannot be uploaded.",
      });
    } else if (ignoredCount > 0) {
      setFeedback({
        tone: "info",
        message: `${pdfFiles.length} PDF ${pdfFiles.length === 1 ? "file is" : "files are"} ready. ${ignoredCount} non-PDF ${ignoredCount === 1 ? "file was" : "files were"} ignored.`,
      });
    }
  };
  const refreshMonthlyWorkflow = async (requestedMonth = month) => {
    setWorkflowRefreshCount((count) => count + 1);
    try {
      await refreshPayrollWorkflowQueries({
        month: requestedMonth,
        refetch: (queryKey, exact) =>
          queryClient.refetchQueries({ queryKey: [...queryKey], exact }),
        invalidate: (queryKey, exact) =>
          queryClient.invalidateQueries({ queryKey: [...queryKey], exact }),
      });
    } finally {
      setWorkflowRefreshCount((count) => Math.max(0, count - 1));
    }
  };

  const pohodaPreviewQuery = useQuery({
    queryKey: ["openproject", "pohoda-preview", month],
    queryFn: () => api.getOpenProjectPohodaPreview(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const pohodaExportsQuery = useQuery({
    queryKey: ["openproject", "pohoda-exports", month],
    queryFn: () => api.getOpenProjectPohodaExports(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const monthChangeLogsQuery = useQuery({
    queryKey: ["openproject", "month-change-logs", month],
    queryFn: () => api.getOpenProjectMonthChangeLogs(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const payslipQuery = useQuery({
    queryKey: ["openproject", "payslips", month],
    queryFn: () => api.getOpenProjectPayslips(month),
    enabled: Boolean(hasPayrollAccess),
    refetchInterval: (query) =>
      query.state.data?.batches?.[0]?.status === "processing" ? 2_000 : false,
  });
  const balanceQuery = useQuery({
    queryKey: ["openproject", "balances", month],
    queryFn: () => api.getOpenProjectBalances(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const balanceState = balanceQuery.data as unknown as
    | (OpenProjectBalanceState & {
        recalculationPending?: boolean;
        exception?: { resultId?: number | null };
        adjustment?: { employeeId?: number | null };
      })
    | undefined;
  const workflowQuery = useQuery({
    queryKey: ["openproject", "payroll-workflow", month],
    queryFn: () => api.getOpenProjectPayrollWorkflow(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const workflowData = workflowQuery.data as unknown as
    | PayrollWorkflowData
    | undefined;
  const payrollProfilesQuery = useQuery({
    queryKey: ["openproject", "payroll-profiles", month],
    queryFn: () => api.getOpenProjectPayrollProfiles(month),
    enabled: Boolean(hasPayrollAccess),
  });
  const adminUsers: AdminUser[] =
    (payrollProfilesQuery.data?.users as unknown as AdminUser[] | undefined) ??
    [];
  const hoursPerDayByEmployee = new Map(
    adminUsers.map((user) => [
      user.displayName,
      payrollHoursPerDayForMonth(user, month),
    ]),
  );
  const hoursPerDayFingerprint = [...hoursPerDayByEmployee.entries()]
    .map(([employee, hoursPerDay]) => `${employee}:${hoursPerDay}`)
    .sort()
    .join("|");
  const periodSummaryQuery = useQuery({
    queryKey: [
      "openproject",
      "balances",
      "period-summary",
      periodFromMonth,
      periodThroughMonth,
    ],
    queryFn: () =>
      api.getOpenProjectPeriodSummary(periodFromMonth, periodThroughMonth),
    enabled: Boolean(hasPayrollAccess),
  });
  const auditQuery = useQuery({
    queryKey: ["openproject", "payroll-audit"],
    queryFn: () => api.getOpenProjectPayrollAudit(),
    enabled: Boolean(hasPayrollAccess),
  });
  const pohodaWorkbookPreviewQuery = useQuery({
    queryKey: [
      "openproject",
      "pohoda-export-file",
      pohodaWorkbookPreviewId,
      month,
      hoursPerDayFingerprint,
    ],
    queryFn: async () => {
      const file = await api.getOpenProjectPohodaExportFile(
        pohodaWorkbookPreviewId!,
      );
      try {
        return {
          file,
          preview: await parseWorkbookPreview(await file.blob.arrayBuffer(), {
            hoursPerDayByEmployee,
          }),
          previewError: null,
        };
      } catch (error) {
        return {
          file,
          preview: null,
          previewError:
            error instanceof Error
              ? error.message
              : "The saved workbook could not be read as XLSX.",
        };
      }
    },
    enabled: Boolean(
      hasPayrollAccess &&
      pohodaWorkbookPreviewId &&
      payrollProfilesQuery.isSuccess,
    ),
    staleTime: Infinity,
    retry: false,
  });
  const monthChangeLogPreviewQuery = useQuery({
    queryKey: [
      "openproject",
      "month-change-log-file",
      monthChangeLogPreviewId,
      month,
    ],
    queryFn: () =>
      api.getOpenProjectMonthChangeLogFile(monthChangeLogPreviewId!),
    enabled: Boolean(hasPayrollAccess && monthChangeLogPreviewId),
    staleTime: Infinity,
    retry: false,
  });
  useEffect(() => {
    const file = monthChangeLogPreviewQuery.data;
    if (!file) {
      setMonthChangeLogPreviewUrl(null);
      return;
    }
    const url = window.URL.createObjectURL(file.blob);
    setMonthChangeLogPreviewUrl(url);
    return () => window.URL.revokeObjectURL(url);
  }, [monthChangeLogPreviewQuery.data]);
  const employeeAuditQuery = useQuery({
    queryKey: [
      "openproject",
      "payroll-audit",
      selectedYearEmployee?.userId,
      periodFromMonth,
      periodThroughMonth,
    ],
    queryFn: () =>
      api.getOpenProjectPayrollAudit({
        level: "changes",
        ...(selectedYearEmployee?.userId !== undefined
          ? { userId: selectedYearEmployee.userId }
          : {}),
        fromMonth: periodFromMonth,
        throughMonth: periodThroughMonth,
      }),
    enabled: Boolean(
      hasPayrollAccess && employeeAuditOpen && selectedYearEmployee,
    ),
  });

  const rowLockMutation = useMutation({
    onMutate: () => {
      bulkLockMutation.reset();
      setFeedback(null);
    },
    mutationFn: async (payload: {
      employeeId: string | number;
      employeeName: string;
      action: "lock" | "unlock";
    }) => {
      if (payload.action === "unlock") {
        return api.temporaryUnlockOpenProjectTimesheetVersion(
          month,
          payload.employeeId,
        );
      }
      return api.generateOpenProjectTimesheetVersion({
        month,
        employeeId: payload.employeeId,
        reason: `Payroll review lock for ${monthLabel(month)}`,
      });
    },
    onSuccess: async (_data, payload) => {
      // The source queries refresh immediately afterwards, but the workflow
      // endpoint can briefly return its pre-lock state. Promote Prepare
      // locally when this was the final lock so the Next action never looks
      // stuck while that authoritative refresh catches up.
      if (
        payload.action === "lock" &&
        lockedTimesheetCount + 1 >= monthlyPayrollUsers.length
      ) {
        queryClient.setQueryData<{
          stages?: AuthoritativePayrollWorkflowStage[];
        }>(["openproject", "payroll-workflow", month], (current) =>
          completePrepareStageAfterTimesheetLock(current, true),
        );
      }
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "success",
        message: `${payload.employeeName}'s timesheet was ${payload.action === "lock" ? "locked" : "unlocked"}.`,
      });
    },
    onError: (error, payload) => {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : `Could not ${payload.action} ${payload.employeeName}'s timesheet.`,
      });
    },
  });
  const overtimeReportMutation = useMutation({
    mutationFn: () => api.downloadOvertimeReport(reportFromMonth, periodThroughMonth),
    onError: (error) => setFeedback({ tone: "error", message: error instanceof Error ? error.message : "Overtime report download failed." }),
  });
  const pohodaExportMutation = useMutation({
    mutationFn: () => api.exportOpenProjectPohoda(month),
    onSuccess: async () => {
      setSelectedWorkflowStage("pohoda");
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "success",
        message: `Pohoda input for ${monthLabel(month)} was generated.`,
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "Pohoda input generation failed.",
      }),
  });
  const viewPohodaMutation = useMutation({
    mutationFn: (exportId: number) => {
      const loadedFile =
        pohodaWorkbookPreviewId === exportId
          ? pohodaWorkbookPreviewQuery.data?.file
          : null;
      return loadedFile
        ? Promise.resolve(downloadFile(loadedFile))
        : api.downloadOpenProjectPohodaExport(exportId);
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "The existing Pohoda input could not be opened.",
      }),
  });
  const viewMonthChangeLogMutation = useMutation({
    mutationFn: (logId: number) => {
      const loadedFile =
        monthChangeLogPreviewId === logId
          ? monthChangeLogPreviewQuery.data
          : null;
      return loadedFile
        ? Promise.resolve(downloadFile(loadedFile))
        : api.downloadOpenProjectMonthChangeLog(logId);
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "The saved month change log could not be opened.",
      }),
  });
  const [bulkProgress, setBulkProgress] = useState("");
  const bulkLockMutation = useMutation({
    mutationKey: ["payroll-bulk-lock", month],
    onMutate: () => setFeedback(null),
    mutationFn: (employeeIds?: number[]) =>
      api.bulkLockOpenProjectTimesheets(
        {
          month,
          employeeIds,
          reason: `Bulk lock from Payroll page for ${monthLabel(month)}`,
        },
        (done, total) =>
          setBulkProgress(`${done} of ${total} employees processed`),
      ),
    onSuccess: async () => {
      setSelectedWorkflowStage(null);
      await refreshMonthlyWorkflow();
      setPendingWorkflowFocus(true);
    },
  });
  const bulkUnlockMutation = useMutation({
    onMutate: () => {
      bulkLockMutation.reset();
      setFeedback(null);
    },
    mutationFn: (reason: string) =>
      api.bulkLockOpenProjectTimesheets(
        { month, unlock: true, reason },
        (done, total) =>
          setBulkProgress(done + " of " + total + " employees processed"),
      ),
    onSuccess: async (data) => {
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: data.failed ? "error" : "success",
        message:
          data.unlocked +
          " unlocked; " +
          data.failed +
          " failed. " +
          data.results
            .filter((item) => item.error)
            .map((item) => item.name + ": " + item.error)
            .join("; "),
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "Unlock failed.",
      }),
  });
  const uploadPayslipsMutation = useMutation({
    mutationFn: ({ month: uploadMonth, files }: { month: string; files: File[] }) =>
      api.uploadOpenProjectPayslips(uploadMonth, files),
    onSuccess: async (data, { month: uploadMonth }) => {
      const summary = (
        data as { uploadSummary?: PayslipUploadSummary } | undefined
      )?.uploadSummary;
      await refreshMonthlyWorkflow(uploadMonth);
      if (currentMonthRef.current !== uploadMonth) return;
      setPayslipFiles([]);
      setReplacementEmployeeName("");
      const duplicateOnly = summary && summary.skippedCount > 0 && summary.acceptedCount === 0 && summary.reviewCount === 0 && summary.rejectedCount === 0;
      setFeedback({
        tone: summary && (summary.reviewCount > 0 || summary.rejectedCount > 0 || duplicateOnly) ? "info" : "success",
        message: duplicateOnly ? "Already uploaded. Existing payslips kept." : summary?.rejectedCount ? "Some PDFs could not be read. Open upload details." : "Upload processed. Review employee payslips below.",
      });
    },
    onError: (error, { month: uploadMonth }) => {
      if (currentMonthRef.current !== uploadMonth) return;
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "Payslip upload failed.",
      });
    },
    onSettled: () => {
      payslipUploadPendingRef.current = false;
    },
  });
  const keepExistingPayslipsMutation = useMutation({
    mutationFn: (batchId: number) =>
      api.keepExistingOpenProjectPayslipsForBatch(batchId, {
        reason:
          "Duplicate upload discarded in one batch; existing payslips retained.",
      }) as Promise<{ keptExistingCount: number }>,
    onSuccess: async (result) => {
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "success",
        message: `${result.keptExistingCount} existing ${result.keptExistingCount === 1 ? "payslip was" : "payslips were"} kept. The duplicate upload was removed from review.`,
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "The duplicate upload could not be removed.",
      }),
  });
  const recoverCrossMonthPayslipsMutation = useMutation({
    mutationFn: (batchId: number) =>
      api.recoverCrossMonthOpenProjectPayslipsForBatch(batchId, {
        reason: `The PDF period matches ${monthLabel(month)}; use this upload for the selected month and remove the wrong-month review records.`,
      }) as Promise<{ recoveredCount: number; sourceMonths: string[] }>,
    onSuccess: async (result) => {
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "success",
        message: `${result.recoveredCount} ${result.recoveredCount === 1 ? "payslip is" : "payslips are"} ready to confirm for ${monthLabel(month)}. The wrong-month review ${result.recoveredCount === 1 ? "record was" : "records were"} removed.`,
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "The payslips could not be moved to the correct month.",
      }),
  });
  const bulkConfirmPayslipsMutation = useMutation({
    mutationFn: () =>
      api.confirmReadyOpenProjectPayslips(month) as Promise<{
        confirmedCount: number;
        attentionCount: number;
        alreadyConfirmedCount: number;
      }>,
    onSuccess: async (result) => {
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "success",
        message:
          result.confirmedCount > 0
            ? `${result.confirmedCount} ${result.confirmedCount === 1 ? "payslip was" : "payslips were"} confirmed.`
            : "No additional payslips were eligible for bulk confirmation.",
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "Ready payslips could not be confirmed.",
      }),
  });
  const resolvePayslipMutation = useMutation({
    mutationFn: (payload: {
      id: number;
      employeeId: string;
      reason: string;
      replaceExisting: boolean;
    }) =>
      api.resolveOpenProjectPayslip(payload.id, {
        employeeId: payload.employeeId,
        confirmedPeriod: month,
        reason: payload.reason,
        replaceExisting: payload.replaceExisting,
      }),
    onSuccess: async () => {
      setPayslipResolveDrafts({});
      await refreshMonthlyWorkflow();
    },
    onError: (error) => {
      if (error instanceof PayrollApiError && error.status === 409) {
        void queryClient.invalidateQueries({
          queryKey: ["openproject", "payslips", month],
        });
      }
    },
  });
  const openPayslipSheet = (
    record: Pick<PayslipRecord, "id">,
    mode: "edit" | "read" = "edit",
  ) => {
    setPayslipSheet({ recordId: record.id, mode });
  };
  const openBalanceResolution = (
    employeeId: number,
    checkKey: BalanceCheckKey,
    trigger?: HTMLButtonElement,
  ) => {
    balanceResolutionTriggerRef.current = trigger ?? null;
    setBalanceResolutionCheckKey(checkKey);
    setBalanceResolutionEmployeeId(employeeId);
  };
  const deletePayslipMutation = useMutation({
    mutationFn: (payload: { record: PayslipRecord; reason: string }) =>
      api.deleteOpenProjectPayslip(payload.record.id, payload.reason, payload.record.correctionRevision ?? 0),
    onSuccess: async () => {
      await refreshMonthlyWorkflow();
    },
  });
  const removeWrongMonthPayslipsMutation = useMutation({
    mutationFn: async ({
      records,
      sourceMonth,
    }: {
      records: PayslipRecord[];
      sourceMonth: string;
    }) => {
      let removed = 0;
      for (const record of records) {
        try {
          await api.deleteOpenProjectPayslip(
            record.id,
            `Wrong-month upload removed from ${monthLabel(sourceMonth)}; the PDF identifies ${monthLabel(record.parsedPeriod)}.`,
            record.correctionRevision ?? 0,
            sourceMonth,
          );
          removed += 1;
        } catch (error) {
          throw new Error(
            `Removed ${removed} of ${records.length} wrong-month payslips. ${error instanceof Error ? error.message : "Removal failed."} Refresh and retry the remaining payslips.`,
          );
        }
      }
      return { removed, sourceMonth };
    },
    onSuccess: ({ removed, sourceMonth }) =>
      setFeedback({
        tone: "success",
        message: `${removed} wrong-month payslip${removed === 1 ? "" : "s"} removed from ${monthLabel(sourceMonth)} review. Upload the correct PDFs to continue. History is preserved.`,
      }),
    onError: (error) => setFeedback({ tone: "error", message: error.message }),
    onSettled: async () => {
      await refreshMonthlyWorkflow();
    },
  });
  const calculateBalanceMutation = useMutation({
    mutationFn: () => api.calculateOpenProjectBalances(month),
    onSuccess: async () => {
      initialBalanceCalculationGuard.complete(month);
      setBalanceCalculationFailure(null);
      await refreshMonthlyWorkflow();
    },
    onError: (error) => {
      const message = payrollLoadErrorMessage(error);
      initialBalanceCalculationGuard.fail(month, message);
      setBalanceCalculationFailure(message);
      return queryClient.invalidateQueries({
        queryKey: ["openproject", "balances", month],
      });
    },
  });
  const calculateBalanceRowMutation = useMutation({
    mutationFn: (payload: { month: string; employeeId: string; runId: number; revision: number }) =>
      api.calculateOpenProjectBalanceRow(payload.month, payload.employeeId, payload.runId, payload.revision),
    onSuccess: async (_data, payload) => { await refreshMonthlyWorkflow(payload.month); },
    onError: async (_error, payload) => { await refreshMonthlyWorkflow(payload.month); },
  });
  const approveBalanceMutation = useMutation({
    mutationFn: (payload: { id: number; revision: number; reason: string }) =>
      api.approveOpenProjectBalanceRun(
        payload.id,
        payload.revision,
        payload.reason,
      ),
    onSuccess: async () => {
      await refreshMonthlyWorkflow();
    },
    onError: async (error) => {
      if (
        error instanceof PayrollApiError &&
        error.code === "balance_run_revision_conflict"
      ) {
        await queryClient.refetchQueries({
          queryKey: ["openproject", "balances", month],
          exact: true,
        });
      }
    },
  });
  const advanceWorkflowMutation = useMutation({
    mutationFn: (stage: Exclude<PayrollWorkflowStageId, "close">) =>
      api.advanceOpenProjectPayrollWorkflow(month, {
        fromStage: stage,
        expectedVersion: Number(workflowData?.version ?? 0),
      }),
    onSuccess: async () => {
      setSelectedWorkflowStage(null);
      await refreshMonthlyWorkflow();
      setPendingWorkflowFocus(true);
    },
    onError: async (error) => {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "Payroll progress changed. Refresh and try again.",
      });
      await queryClient.invalidateQueries({
        queryKey: ["openproject", "payroll-workflow", month],
      });
    },
  });
  const closeMonthMutation = useMutation({
    mutationFn: () => api.closeOpenProjectPayrollMonth(month),
    onSuccess: async () => {
      await refreshMonthlyWorkflow();
      await queryClient.invalidateQueries({
        queryKey: ["openproject", "month-change-logs", month],
      });
      setFeedback({
        tone: "success",
        message: `${monthLabel(month)} payroll was closed and published to Period Summary.`,
      });
    },
    onError: (error) => {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : "The payroll month could not be closed.",
      });
    },
  });
  const reopenMonthMutation = useMutation({
    mutationFn: (payload: { stage: PayrollWorkflowStageId; reason: string }) =>
      api.reopenOpenProjectPayrollMonth(month, {
        fromStage: payload.stage,
        reason: payload.reason,
        confirmed: true,
      }),
    onSuccess: async () => {
      setReopenStage(null);
      setReopenReason("");
      await refreshMonthlyWorkflow();
      setFeedback({
        tone: "info",
        message:
          "The month was reopened. Dependent calculations, approval, and closure are no longer current.",
      });
    },
  });
  const [overtimeEditEmployeeId, setOvertimeEditEmployeeId] = useState<number | null>(null);
  const [uploadRemovalRecord, setUploadRemovalRecord] = useState<PayslipRecord | null>(null);
  const [uploadRemovalReason, setUploadRemovalReason] = useState("");
  const [emailSendResult, setEmailSendResult] = useState<string | null>(null);
  const [emailDeliveries, setEmailDeliveries] = useState<Array<
    Record<string, unknown>
  > | null>(null);
  const refreshEmailDeliveries = async () => {
    try {
      const data = (await api.getOpenProjectPayrollEmailDeliveries(
        month,
      )) as unknown;
      setEmailDeliveries(Array.isArray(data) ? data : []);
    } catch {
      setEmailDeliveries([]);
    }
  };
  const sendPayrollEmailsMutation = useMutation({
    mutationFn: (mode: "dry_run" | "live") => api.sendOpenProjectPayrollEmails(month, mode),
    onSuccess: async (data, deliveryMode) => {
      const value = data as unknown as {
        employeeSentCount?: number;
        employeeAlreadySentCount?: number;
        employeeFailedCount?: number;
        employeeSkippedCount?: number;
        office?: { status?: string };
      };
      const count = value.employeeSentCount ?? 0;
      const officeStatus = value.office?.status;
      const office = officeStatus === "sent" ? ", office copy sent" : "";
      setEmailSendResult(
        `${count} employee email${count === 1 ? "" : "s"} sent, ${value.employeeAlreadySentCount ?? 0} already sent, ${value.employeeFailedCount ?? 0} failed, ${value.employeeSkippedCount ?? 0} skipped${office}.${deliveryMode === "dry_run" ? " Dry run: copies sent to the office only; employee delivery history unchanged." : ""}`,
      );
      await refreshEmailDeliveries();
      await queryClient.invalidateQueries({
        queryKey: ["openproject", "email-delivery-preview", month],
      });
    },
    onError: (error) => {
      setEmailSendResult(
        error instanceof Error
          ? `Email sending failed: ${error.message}`
          : "Email sending failed.",
      );
    },
  });
  const emailSentCount = emailDeliveries
    ? emailDeliveries.filter((entry) => entry["status"] === "sent").length
    : null;
  const emailSimulatedCount = emailDeliveries
    ? emailDeliveries.filter((entry) => entry["status"] === "simulated")
        .length
    : null;
  useEffect(() => {
    if (workflowData?.closure?.status === "closed") void refreshEmailDeliveries();
    else setEmailDeliveries(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData?.closure?.status, month]);
  const pohodaPreview = pohodaPreviewQuery.data as unknown as
    | (Omit<OpenProjectPohodaPreview, "employees"> & {
        latestExportId?: number | null;
        employees?: Array<
          OpenProjectPohodaPreview["employees"][number] & {
            employeeId?: number;
          }
        >;
      })
    | undefined;
  const pohodaDataQuality = pohodaPreview?.dataQuality;
  const pohodaExports: PohodaExportVersionSummary[] =
    (pohodaExportsQuery.data?.exports as unknown as
      | PohodaExportVersionSummary[]
      | undefined) ?? [];
  const monthChangeLogs: MonthChangeLogEntry[] =
    (monthChangeLogsQuery.data?.["versions"] as
      | MonthChangeLogEntry[]
      | undefined) ?? [];
  const latestPohodaExport = pohodaExports[0] ?? null;
  const payslipRecords: PayslipRecord[] =
    (payslipQuery.data?.records as unknown as PayslipRecord[] | undefined) ??
    [];
  const payslipBatches: PayslipBatch[] =
    (payslipQuery.data?.batches as unknown as PayslipBatch[] | undefined) ?? [];
  const calculationReadiness: CalculationReadiness | null =
    balanceState?.readiness ?? null;
  const latestBalanceRun: (BalanceRun & { revision: number }) | null =
    (balanceState?.latestRun as unknown as
      | (BalanceRun & {
          revision: number;
        })
      | null) ?? null;
  const requestBalanceCalculation = () => {
    if (!latestBalanceRun && !initialBalanceCalculationGuard.retry(month))
      return;
    setBalanceCalculationFailure(null);
    calculateBalanceMutation.reset();
    calculateBalanceMutation.mutate();
  };
  const recalculationPending = balanceState?.recalculationPending === true;
  const recalculationRetryResultId = Number(
    balanceState?.exception?.resultId ?? NaN,
  );
  const recalculationRetryUserId = Number(
    balanceState?.adjustment?.employeeId ?? NaN,
  );
  const calculationRows = [...(latestBalanceRun?.results ?? [])].sort((left, right) =>
    left.employeeName.localeCompare(right.employeeName, undefined, { sensitivity: "base" }) || String(left.profileId).localeCompare(String(right.profileId)));
  const showCalculationPaidLeave = calculationRows.some((result) =>
    Boolean(result.crossChecks?.["paidLeave"]),
  );
  const approvalBlockingLog = buildApprovalBlockingLog(calculationRows);
  const approvalBlockingCheckCount = approvalBlockingLog.reduce(
    (total, employee) => total + employee.issues.length,
    0,
  );
  const visibleCalculationRows = hideZeroValueCalculationRows
    ? calculationRows.filter((result) =>
        result.recalculationRequired || hasCalculationValues(result, displayUnit),
      )
    : calculationRows;
  const hiddenZeroValueCalculationCount =
    calculationRows.length - visibleCalculationRows.length;
  const activePayrollUsers = adminUsers.filter((user) => user.isActive);
  const monthlyPayrollUsers = authoritativePayrollUsersForMonth(
    activePayrollUsers,
    month,
  );
  const pendingCalculationRows = latestBalanceRun ? monthlyPayrollUsers.filter(user =>
    !calculationRows.some(result => result.profileId === String(user.id) || result.userId === user.privateEmployeeId || String(result.userId) === String(user.id)),
  ).map(user => ({ pending: true as const, id: `pending-${user.id}`, profileId: String(user.id), employeeName: user.displayName || user.username })) : [];
  const calculationLedgerRows = [...visibleCalculationRows, ...pendingCalculationRows].sort((left, right) =>
    left.employeeName.localeCompare(right.employeeName, undefined, { sensitivity: "base" }) || String(left.profileId).localeCompare(String(right.profileId)));
  const staleCalculationCount = calculationLedgerRows.filter(row => "pending" in row || row.recalculationRequired).length;
  const missingPayrollCodeUsers = monthlyPayrollUsers.filter(
    (user) => !user.payrollEmployeeCode?.trim(),
  );
  const timesheetVersionQueries = useQueries({
    queries: monthlyPayrollUsers.map((user) => ({
      queryKey: ["openproject", "timesheet-versions", month, user.id],
      queryFn: () =>
        api.getOpenProjectTimesheetVersions(
          month,
          user.id,
        ) as Promise<TimesheetVersionState>,
      enabled: Boolean(hasPayrollAccess),
      retry: false,
      staleTime: 15_000,
    })),
  });
  const timesheetStateByUserId = new Map<
    string | number,
    TimesheetVersionState | undefined
  >(
    monthlyPayrollUsers.map((user, index) => [
      user.id,
      timesheetVersionQueries[index]?.data,
    ]),
  );
  const balanceResolutionResult = balanceResolutionEmployeeId
    ? (calculationRows.find(
        (result) => result.userId === balanceResolutionEmployeeId,
      ) ?? null)
    : null;
  const overtimeEditResult = calculationRows.find((row) => row.userId === overtimeEditEmployeeId);
  const balanceResolutionTimesheet = balanceResolutionResult
    ? timesheetStateByUserId.get(balanceResolutionResult.profileId)
    : undefined;
  const timesheetStatesLoading = timesheetVersionQueries.some(
    (query) => query.isPending,
  );
  const lockedTimesheetCount = monthlyPayrollUsers.filter(
    (user) => timesheetStateByUserId.get(user.id)?.locked,
  ).length;
  const timesheetLockSummary = summarizeTimesheetLocks(
    monthlyPayrollUsers.map((user) => timesheetStateByUserId.get(user.id)),
  );
  const readyTimesheetCount = timesheetLockSummary.ready;
  const latestPayslipBatch = payslipBatches[0] ?? null;
  const latestPayslipFileResults = latestPayslipBatch?.fileResults ?? [];
  const payslipActionBatch = selectLatestPayslipActionBatch(
    payslipBatches,
    payslipRecords,
  );
  const latestPayslipDuplicateRecords = payslipActionBatch
    ? payslipRecords.filter(
        (record) =>
          record.batchId === payslipActionBatch.id &&
          record.matchStatus === "duplicate" &&
          record.duplicateOfRecordId !== null,
      )
    : [];
  const latestCrossMonthDuplicateRecords = latestPayslipDuplicateRecords.filter(
    (record) =>
      payslipDuplicateDisposition(record) === "recoverable-cross-month",
  );
  const latestSameMonthDuplicateRecords = latestPayslipDuplicateRecords.filter(
    (record) => payslipDuplicateDisposition(record) === "same-month",
  );
  const latestProtectedCrossMonthDuplicateRecords =
    latestPayslipDuplicateRecords.filter(
      (record) =>
        payslipDuplicateDisposition(record) === "protected-cross-month",
    );
  const latestProtectedCrossMonthSourceMonths = [
    ...new Set(
      latestProtectedCrossMonthDuplicateRecords
        .map((record) => record.duplicateSource?.month)
        .filter((value): value is string => Boolean(value)),
    ),
  ];
  const latestCrossMonthSourceMonths = [
    ...new Set(
      latestCrossMonthDuplicateRecords
        .map((record) => record.duplicateSource?.month)
        .filter((value): value is string => Boolean(value)),
    ),
  ];
  const inactiveUploadRecordIds = new Set([
    ...latestPayslipFileResults.filter(result => result.currentStatus === "ignored" || result.currentStatus === "replaced").map(result => result.recordId),
    ...payslipRecords
      .filter(
        (record) =>
          record.matchStatus === "ignored" ||
          record.matchStatus === "replaced" ||
          record.replacedByRecordId,
      )
      .map((record) => record.id),
  ]);
  const activeLatestPayslipFileResults = latestPayslipFileResults.filter(
    (result) => !result.recordId || !inactiveUploadRecordIds.has(result.recordId),
  );
  const latestUploadRecordIds = new Set(
    activeLatestPayslipFileResults.map((result) => result.recordId),
  );
  const wrongMonthUploadRecords = payslipRecords.filter(
    (record) =>
      latestUploadRecordIds.has(record.id) &&
      record.matchStatus === "period_mismatch" &&
      /^20\d{2}-(0[1-9]|1[0-2])$/.test(record.parsedPeriod) &&
      record.parsedPeriod !== month,
  );
  const removableWrongMonthRecords = wrongMonthUploadRecords.filter(
    (record) => !record.confirmedAt && (record.correctionRevision ?? 0) === 0,
  );
  const wrongMonthUploadPeriods = [
    ...new Set(wrongMonthUploadRecords.map((record) => record.parsedPeriod)),
  ].sort();
  const latestPayslipRows = latestPayslipFileResults.map((result) => {
    const record = result.recordId
      ? (payslipRecords.find((candidate) => candidate.id === result.recordId) ??
        null)
      : null;
    const status =
      result.currentStatus === "ignored" || record?.matchStatus === "ignored"
        ? "Removed"
        : result.currentStatus === "replaced" || record?.matchStatus === "replaced" || record?.replacedByRecordId
          ? "Replaced"
          : !record && result.recordId ? "Upload history"
          : record?.matchStatus === "ineligible" ? "Not in this payroll"
          : record?.matchStatus === "period_mismatch"
            ? "Wrong month"
            : result.outcome === "skipped"
              ? result.code === "existing_payslip_kept"
                ? "Existing kept"
                : "Already uploaded"
              : result.outcome === "rejected"
                ? "Rejected"
                : record && payslipRequiresReplacement(record)
                  ? "Replacement required"
                  : result.outcome === "needs_review"
                    ? "Needs checking"
                    : record?.calculationReady
                      ? "Confirmed"
                      : record?.usable
                        ? "Ready to confirm"
                        : "Accepted";
    return {
      result,
      record,
      status,
      category: payslipReviewCategoryForStatus(status),
    };
  });
  const latestPayslipUploadCounts = {
    removed: latestPayslipRows.filter(({ status }) => status === "Removed").length,
    replaced: latestPayslipRows.filter(({ status }) => status === "Replaced").length,
    notInPayroll: latestPayslipRows.filter(({ status }) => status === "Not in this payroll").length,
    accepted: activeLatestPayslipFileResults.filter(
      (result) => result.outcome === "accepted",
    ).length,
    review: latestPayslipRows.filter(({ status }) =>
      ["Needs checking", "Wrong month", "Replacement required"].includes(status),
    ).length,
    rejected: activeLatestPayslipFileResults.filter(
      (result) => result.outcome === "rejected",
    ).length,
    skipped: activeLatestPayslipFileResults.filter(
      (result) => result.outcome === "skipped",
    ).length,
  };
  const latestPayslipUploadKeptExisting =
    latestPayslipFileResults.length > 0 &&
    latestPayslipUploadCounts.skipped === latestPayslipFileResults.length &&
    latestPayslipUploadCounts.accepted === 0 &&
    latestPayslipUploadCounts.review === 0 &&
    latestPayslipUploadCounts.rejected === 0;
  const payslipReviewCategoryOrder: Record<PayslipReviewCategory, number> = {
    attention: 0,
    ready: 1,
    complete: 2,
  };
  const filteredLatestPayslipRows = latestPayslipRows
    .filter(({ category }) => {
      if (payslipListFilter === "all") return true;
      return category === payslipListFilter;
    })
    .sort(
      (left, right) =>
        payslipReviewCategoryOrder[left.category] -
        payslipReviewCategoryOrder[right.category],
    );
  const yearSummaryRows: YearSummaryEmployee[] =
    (periodSummaryQuery.data?.employees as unknown as
      | YearSummaryEmployee[]
      | undefined) ?? [];
  const visibleYearSummaryRows = hideZeroValueEmployees
    ? yearSummaryRows.filter((row) => hasPeriodSummaryValues(row, displayUnit))
    : yearSummaryRows;
  const hiddenZeroValueEmployeeCount =
    yearSummaryRows.length - visibleYearSummaryRows.length;
  const selectedYearEmployeeTotals = selectedYearEmployee
    ? yearDetailTotals(selectedYearEmployee.monthlyRows ?? [])
    : null;
  const showPeriodPaidLeave = yearSummaryRows.some((employee) =>
    employee.monthlyRows.some(hasPaidLeaveEvidence),
  );
  const selectedPeriodMonths = [
    ...(selectedYearEmployee?.monthlyRows ?? []),
  ].sort((left, right) => right.month.localeCompare(left.month));
  const hasPeriodRecord = (row: YearSummaryMonth) =>
    row.hasResult ||
    Boolean(row.closed && row.payslipAvailable && row.payslipRecordId);
  const emptyPeriodMonthCount = selectedPeriodMonths.filter(
    (row) => !hasPeriodRecord(row),
  ).length;
  const visiblePeriodMonths = showEmptyPeriodMonths
    ? selectedPeriodMonths
    : selectedPeriodMonths.filter(hasPeriodRecord);
  const showSelectedPeriodPaidLeave =
    selectedPeriodMonths.some(hasPaidLeaveEvidence);
  const openValueCorrection = (
    row: YearSummaryMonth,
    fieldKey: ValueCorrectionFieldKey,
    trigger: HTMLButtonElement,
  ) => {
    if (!selectedYearEmployee) return;
    setValueCorrectionTarget({
      userId: selectedYearEmployee.userId,
      employeeName: selectedYearEmployee.employeeName,
      month: row.month,
      periodThrough: periodThroughMonth,
      fieldKey,
      displayUnit,
      hoursPerDay: row.hoursPerDay,
      trigger,
    });
  };
  const auditEvents = normalizeAuditEvents(auditQuery.data?.events);
  const employeeAuditEvents = normalizeAuditEvents(
    employeeAuditQuery.data?.events,
  ).filter(
    (event: AuditEvent) => event.action === "balance_adjustment.created",
  );
  const latestPohodaExportEvent = auditEvents.find((event) => {
    const eventMonth = String(
      auditValue(event, "month") ?? event.afterPayload?.["month"] ?? "",
    );
    return event.action === "pohoda_export.generated" && eventMonth === month;
  });
  const latestPohodaExportId = Number(
    latestPohodaExport?.id ?? pohodaPreview?.latestExportId ?? NaN,
  );
  const usablePayslipRecords = payslipRecords.filter(
    (record) => record.usable && record.calculationReady,
  );
  const exceptionPayslipRecords = payslipRecords.filter(payslipNeedsAttention);
  const readyToConfirmPayslipRecords = payslipRecords.filter(
    (record) =>
      record.usable &&
      !record.calculationReady &&
      !payslipNeedsAttention(record),
  );
  const locallyConfirmedPayslipEmployeeCount = new Set(
    usablePayslipRecords.map((record) => record.employee?.id).filter(Boolean),
  ).size;
  const payslipProgressKey = (record: PayslipRecord) => {
    if (record.employee?.id) return `employee:${record.employee.id}`;

    const parsedName = record.parsedEmployeeName?.trim().toLocaleLowerCase();
    return parsedName ? `name:${parsedName}` : `record:${record.id}`;
  };
  const attentionPayslipKeys = new Set(
    exceptionPayslipRecords.map(payslipProgressKey),
  );
  const confirmedPayslipKeys = new Set(
    usablePayslipRecords.map(payslipProgressKey),
  );
  const readyToConfirmPayslipEmployeeCount = new Set(
    readyToConfirmPayslipRecords
      .filter((record) => {
        const key = payslipProgressKey(record);
        return !attentionPayslipKeys.has(key) && !confirmedPayslipKeys.has(key);
      })
      .map((record) => record.employee?.id ?? record.parsedEmployeeName)
      .filter(Boolean),
  ).size;
  const readyPayslipKeys = new Set(
    readyToConfirmPayslipRecords
      .map(payslipProgressKey)
      .filter(
        (key) =>
          !attentionPayslipKeys.has(key) && !confirmedPayslipKeys.has(key),
      ),
  );
  const reviewedPayslipKeys = new Set(
    [...confirmedPayslipKeys].filter(
      (key) => !attentionPayslipKeys.has(key) && !readyPayslipKeys.has(key),
    ),
  );
  const payslipProgressTotal = monthlyPayrollUsers.length;
  const payslipAttentionProgressCount = Math.min(
    payslipProgressTotal,
    attentionPayslipKeys.size,
  );
  const payslipReadyProgressCount = Math.min(
    Math.max(0, payslipProgressTotal - payslipAttentionProgressCount),
    readyPayslipKeys.size,
  );
  const payslipReviewedProgressCount = Math.min(
    Math.max(
      0,
      payslipProgressTotal -
        payslipAttentionProgressCount -
        payslipReadyProgressCount,
    ),
    reviewedPayslipKeys.size,
  );
  const authoritativePayslipProgress = resolveAuthoritativePayslipProgress(
    workflowData?.payslipProgress,
    {
      eligibleCount: payslipProgressTotal,
      confirmedCount: locallyConfirmedPayslipEmployeeCount,
      missingCount: Math.max(
        0,
        payslipProgressTotal - payslipReviewedProgressCount,
      ),
    },
  );
  const workflowPayslipProgressVerified = hasAuthoritativePayslipProgress(
    workflowData?.payslipProgress,
  );
  const payslipListContradictsWorkflow =
    payslipQuery.isSuccess &&
    workflowPayslipProgressVerified &&
    authoritativePayslipProgress.eligibleCount === payslipProgressTotal &&
    authoritativePayslipProgress.missingCount === 0 &&
    locallyConfirmedPayslipEmployeeCount < payslipProgressTotal;
  const displayedPayslipProgress = payslipListContradictsWorkflow
    ? {
        eligibleCount: payslipProgressTotal,
        confirmedCount: locallyConfirmedPayslipEmployeeCount,
        missingCount: Math.max(
          0,
          payslipProgressTotal - locallyConfirmedPayslipEmployeeCount,
        ),
      }
    : authoritativePayslipProgress;
  const confirmedPayslipEmployeeCount = displayedPayslipProgress.confirmedCount;
  const payslipsConfirmed =
    workflowPayslipProgressVerified &&
    displayedPayslipProgress.eligibleCount > 0 &&
    displayedPayslipProgress.missingCount === 0;
  const payslipAttentionDetails = Array.from(
    new Set(
      exceptionPayslipRecords.map((record) => {
        const employeeName =
          record.employee?.displayName ||
          record.parsedEmployeeName ||
          record.sourceFileName;
        return `${employeeName} - ${payslipIssueContext(record).title}`;
      }),
    ),
  );
  const activePayslipQueueIndex = Math.min(
    payslipQueueIndex,
    Math.max(0, exceptionPayslipRecords.length - 1),
  );
  const exceptionPayslipIssueCount = new Set(
    exceptionPayslipRecords.map((record) => payslipIssueContext(record).title),
  ).size;
  const payrollDataQueries = [
    { label: "Work profiles", query: payrollProfilesQuery },
    { label: "Pohoda readiness", query: pohodaPreviewQuery },
    { label: "Saved Pohoda versions", query: pohodaExportsQuery },
    { label: "Payslips", query: payslipQuery },
    { label: "Balance state", query: balanceQuery },
    { label: "Workflow state", query: workflowQuery },
  ];
  const failedPayrollDataQuery = payrollDataQueries.find(
    ({ query }) => query.isError,
  );
  const payrollDataPending = payrollDataQueries.some(({ query }) => query.isPending);
  const failedWorkflowDataQuery = workflowQuery.isError
    ? { label: "Workflow state", query: workflowQuery }
    : undefined;
  const authoritativeWorkflowStages = Array.isArray(workflowData?.stages)
    ? (workflowData.stages as AuthoritativePayrollWorkflowStage[])
    : null;
  const workflowStateUnavailable =
    workflowQuery.isPending ||
    workflowQuery.isError ||
    !authoritativeWorkflowStages;
  const derivedWorkflowStages = derivePayrollWorkflow({
    employeeCount: monthlyPayrollUsers.length,
    lockedCount: lockedTimesheetCount,
    readyForPohodaCount: pohodaPreview?.readyCount ?? 0,
    pohodaExported: Boolean(latestPohodaExport || latestPohodaExportEvent),
    payslipRecordCount: payslipRecords.length,
    confirmedPayslipCount: confirmedPayslipEmployeeCount,
    blockingPayslipCount: displayedPayslipProgress.missingCount,
    payslipAttentionDetails,
    readyToConfirmPayslipCount: readyToConfirmPayslipEmployeeCount,
    payslipProcessing: latestPayslipBatch?.status === "processing",
    calculationRun: latestBalanceRun
      ? {
          status: latestBalanceRun.status,
          employeeCount: latestBalanceRun.employeeCount,
          redCount: approvalBlockingCheckCount,
          approvedCount: latestBalanceRun.results.filter(
            (result) => result.approved,
          ).length,
        }
      : null,
    closed: workflowData?.closure?.status === "closed",
  });
  const workflowStages: PayrollWorkflowStage[] = workflowStateUnavailable
    ? derivedWorkflowStages.map((stage) => ({
        ...stage,
        state: "blocked" as const,
        summary:
          stage.id === "prepare"
            ? failedWorkflowDataQuery
              ? "Payroll progress cannot be verified until the workflow service is available."
              : "Loading the authoritative workflow state…"
            : stage.summary,
        blocker: failedWorkflowDataQuery
          ? "Workflow state must be available before continuing."
          : "Waiting for the authoritative workflow state.",
        interactive: false,
      }))
    : (guardPayrollWorkflowAgainstPayslipMismatch(
        applyAuthoritativePayrollWorkflow(
          derivedWorkflowStages,
          authoritativeWorkflowStages as AuthoritativePayrollWorkflowStage[],
        ) as unknown as PayrollWorkflowStage[],
        payslipListContradictsWorkflow,
      ) as unknown as PayrollWorkflowStage[]);
  const recommendedStage = recommendedPayrollStage(workflowStages);
  const activeWorkflowStageId = resolveActivePayrollWorkflowStageId({
    stages: workflowStages,
    selectedId: selectedWorkflowStage,
    recommendedId: recommendedStage.id,
  });
  const activeWorkflowStage =
    workflowStages.find((stage) => stage.id === activeWorkflowStageId) ??
    recommendedStage;
  const stageRequirementsComplete = (id: PayrollWorkflowStageId) =>
    workflowStages.find((stage) => stage.id === id)?.requirementsComplete ===
    true;
  const canGeneratePohoda = stageRequirementsComplete("prepare");
  const monthClosed = workflowData?.closure?.status === "closed";
  const canUploadPayslips = stageRequirementsComplete("pohoda") && !monthClosed;
  const calculationInputsReady = calculationReadiness?.ready === true;
  const canCalculateBalances =
    stageRequirementsComplete("payslips") && calculationInputsReady;
  const isWorkflowRefreshing = workflowRefreshCount > 0;
  const emailPreviewQuery = useQuery({
    queryKey: ["openproject", "email-delivery-preview", month],
    queryFn: () => api.getOpenProjectPayrollEmailDeliveryPreview(month),
    enabled:
      Boolean(hasPayrollAccess) && activeWorkflowStage?.id === "close",
    staleTime: 30_000,
  });
  const emailPreview = emailPreviewQuery.data as unknown as
    | {
        sender?: string;
        readyToClose?: boolean;
        closeBlocker?: string;
        employeeReadyCount?: number;
        employeeRequiredCount?: number;
        officeSent?: boolean;
        employeeRecipients?: Array<{
          employeeId: number;
          name: string;
          email: string;
          pdfPasswordConfigured: boolean;
        }>;
        officeRecipients?: string[];
        officePdfEncrypted?: false;
      }
    | undefined;
  const emailConfirmation = (mode: "dry_run" | "live"): ConfirmationState => {
    const preview = emailPreview;
    const dryRun = mode === "dry_run";
    return {
      title: dryRun ? `Send ${monthLabel(month)} review copies to the office?` : `Send ${monthLabel(month)} payroll emails to employees?`,
      description: [
        dryRun ? "Dry run sends each employee's email to the office with an unencrypted PDF. It does not mark employee emails as sent."
          : "Protected payslips go to employees; the office receives the combined summary.",
        `Sender: ${preview?.sender ?? "—"}`,
        `${dryRun ? "Office review copies" : "Employee recipients"} (${preview?.employeeRecipients?.length ?? 0}): ${preview?.employeeRecipients?.map(recipient => recipient.name).join(", ") || "none"}`,
        `Office recipients: ${preview?.officeRecipients?.join(", ") || "none configured — configure an office address before sending"}`,
      ].join("\n"),
      confirmLabel: dryRun ? "Send dry run to office" : "Send real payroll emails",
      onConfirm: () => sendPayrollEmailsMutation.mutate(mode),
    };
  };
  const initialBalanceCalculationState =
    initialBalanceCalculationGuard.state(month);
  const automaticBalanceCalculationPending =
    activeWorkflowStageId === "reconcile" &&
    canCalculateBalances &&
    !latestBalanceRun &&
    !recalculationPending &&
    !calculateBalanceMutation.isError &&
    (calculateBalanceMutation.isPending ||
      initialBalanceCalculationState.status === "in_flight");
  const manualRecalculationAvailable =
    activeWorkflowStageId === "reconcile" &&
    canCalculateBalances &&
    (Boolean(latestBalanceRun && latestBalanceRun.status !== "approved") ||
      recalculationPending ||
      calculateBalanceMutation.isError ||
      initialBalanceCalculationState.status === "failed");
  const canReviewCalculationDetails =
    calculationInputsReady && Boolean(latestBalanceRun);
  const canApproveCalculation =
    calculationInputsReady &&
    !calculateBalanceRowMutation.isPending &&
    pendingCalculationRows.length === 0 &&
    !calculationRows.some(result => result.recalculationRequired) &&
    approvalBlockingCheckCount === 0 &&
    latestBalanceRun?.status !== "approved";
  const canReviewCompletion = stageRequirementsComplete("reconcile");
  const monthStatus = workflowQuery.isFetching || workflowQuery.isPending
    ? "checking"
    : workflowQuery.isError
      ? "unavailable"
      : workflowData && "closure" in workflowData
        ? monthClosed ? "closed" : "open"
        : "unknown";
  const monthStatuses: Record<string, "open" | "closed"> = {};
  if (periodSummaryQuery.isSuccess) {
    for (const employee of yearSummaryRows) {
      for (const row of employee.monthlyRows ?? []) {
        if (typeof row.closed === "boolean") {
          monthStatuses[row.month] = monthStatuses[row.month] === "closed" || row.closed
            ? "closed"
            : "open";
        }
      }
    }
  }
  if (monthStatus === "open" || monthStatus === "closed") {
    monthStatuses[month] = monthStatus;
  } else {
    delete monthStatuses[month];
  }
  const reopenStageIndex = reopenStage
    ? workflowStages.findIndex((stage) => stage.id === reopenStage)
    : -1;
  const reopenStageTitle =
    reopenStageIndex >= 0 ? workflowStages[reopenStageIndex]?.title : null;
  const invalidatedStageTitles =
    reopenStageIndex >= 0
      ? workflowStages.slice(reopenStageIndex + 1).map((stage) => stage.title)
      : [];
  const workflowHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const previousRecommendedStageRef = useRef<{
    month: string;
    id: PayrollWorkflowStageId;
  } | null>(null);

  const focusWorkflowStage = () => {
    window.requestAnimationFrame(() => {
      workflowHeadingRef.current?.focus({ preventScroll: true });
      workflowHeadingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const selectWorkflowStage = (id: PayrollWorkflowStageId) => {
    setSelectedWorkflowStage(id);
    focusWorkflowStage();
  };

  useEffect(() => {
    const previousMonth = previousWorkflowMonthRef.current;
    previousWorkflowMonthRef.current = month;
    if (!shouldResetPayrollWorkflowSelection(previousMonth, month)) return;
    setPayslipQueueIndex(0);
    setPayslipFiles([]);
    setPayslipDropActive(false);
    setReplacementEmployeeName("");
    setFeedback(null);
    setBalanceCalculationFailure(
      initialBalanceCalculationGuard.state(month).error,
    );
    setSelectedWorkflowStage(null);
    setPendingWorkflowFocus(false);
  }, [month]);

  useEffect(() => () => initialBalanceCalculationGuard.release(month), [month]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        PAYROLL_DISPLAY_UNIT_STORAGE_KEY,
        displayUnit,
      );
    } catch {
      // The selector still works when browser storage is unavailable.
    }
  }, [displayUnit]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (periodSummaryOnly) {
      for (const key of ["month", "stage", "payslipStep", "periodThrough"]) url.searchParams.delete(key);
      url.searchParams.set("from", reportFromMonth);
      url.searchParams.set("through", periodThroughMonth);
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      try { window.sessionStorage.setItem(periodSummaryContextStorageKey(payrollPrincipalId), new URLSearchParams({ from: reportFromMonth, through: periodThroughMonth }).toString()); } catch { /* The report still works without browser storage. */ }
      return;
    }
    url.searchParams.set("month", month);
    url.searchParams.set("stage", activeWorkflowStageId);
    if (activeWorkflowStageId === "payslips")
      url.searchParams.set("payslipStep", payslipSubstep);
    else url.searchParams.delete("payslipStep");
    url.searchParams.set("periodThrough", periodThroughMonth);
    url.searchParams.delete("employee");
    const nextHistoryState = { ...(window.history.state ?? {}), payrollContextPrincipalId: payrollPrincipalId };
    delete nextHistoryState.payrollProfileRecovery;
    window.history.replaceState(
      nextHistoryState,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    try {
      const storedContext = new URLSearchParams({
        principal: payrollPrincipalId,
        month,
        periodThrough: periodThroughMonth,
        stage: activeWorkflowStageId,
      });
      if (activeWorkflowStageId === "payslips")
        storedContext.set("payslipStep", payslipSubstep);
      window.sessionStorage.setItem(
        payrollContextStorageKey(payrollPrincipalId),
        storedContext.toString(),
      );
    } catch {
      // Payroll still works when session storage is unavailable.
    }
    window.dispatchEvent(new Event("payroll-context-change"));
  }, [
    activeWorkflowStageId,
    month,
    payrollPrincipalId,
    payslipSubstep,
    periodThroughMonth,
    periodSummaryOnly,
    reportFromMonth,
  ]);

  useEffect(() => {
    const restorePayrollState = () => {
      if (periodSummaryOnly) {
        const params = readPeriodSummaryParams(payrollPrincipalId);
        const through = validPeriodThroughOr(params.get("through"), currentLocalYYYYMM());
        setPeriodThroughMonth(through);
        setReportFromMonth(validPeriodThroughOr(params.get("from"), `${through.slice(0, 4)}-01`));
        return;
      }
      const params = readPayrollSearchParams(payrollPrincipalId);
      setMonth(validMonthOr(params.get("month"), currentLocalYYYYMM()));
      setSelectedWorkflowStage(validWorkflowStageOrNull(params.get("stage")));
      setPayslipSubstep(validPayslipSubstep(params.get("payslipStep")));
      setPeriodThroughMonth(
        validPeriodThroughOr(params.get("periodThrough"), currentLocalYYYYMM()),
      );
    };
    window.addEventListener("popstate", restorePayrollState);
    return () => window.removeEventListener("popstate", restorePayrollState);
  }, [payrollPrincipalId, periodSummaryOnly]);

  useEffect(() => {
    // Hydration establishes the starting stage; it is not an operator advancing
    // the workflow and must not steal focus or scroll past the page heading.
    if (payrollDataPending || workflowStateUnavailable) {
      previousRecommendedStageRef.current = null;
      return;
    }
    const previousRecommendedStage = previousRecommendedStageRef.current;
    previousRecommendedStageRef.current = { month, id: recommendedStage.id };
    if (
      previousRecommendedStage?.month === month &&
      previousRecommendedStage.id !== recommendedStage.id
    ) {
      const waitsForExplicitNext =
        ["prepare", "pohoda"].includes(previousRecommendedStage.id) &&
        selectedWorkflowStage === previousRecommendedStage.id;
      if (waitsForExplicitNext) {
        setPendingWorkflowFocus(false);
      } else if (
        !selectedWorkflowStage ||
        selectedWorkflowStage === previousRecommendedStage.id
      ) {
        setSelectedWorkflowStage(null);
        setPendingWorkflowFocus(true);
      }
    }
  }, [month, payrollDataPending, recommendedStage.id, selectedWorkflowStage, workflowStateUnavailable]);

  useEffect(() => {
    if (
      !pendingWorkflowFocus ||
      confirmation ||
      payslipSheet ||
      reopenStage ||
      pohodaWorkbookPreviewId ||
      monthChangeLogPreviewId
    )
      return;
    focusWorkflowStage();
    setPendingWorkflowFocus(false);
  }, [
    activeWorkflowStageId,
    confirmation,
    payslipSheet,
    pendingWorkflowFocus,
    pohodaWorkbookPreviewId,
    monthChangeLogPreviewId,
    reopenStage,
  ]);

  useEffect(() => {
    if (!canReviewCalculationDetails) setCalculationDetailsOpen(false);
  }, [canReviewCalculationDetails]);

  useEffect(() => {
    if (!latestBalanceRun) return;
    initialBalanceCalculationGuard.complete(month);
    setBalanceCalculationFailure(null);
  }, [latestBalanceRun, month]);

  useEffect(() => {
    const readyForInitialCalculation =
      !periodSummaryOnly &&
      activeWorkflowStageId === "reconcile" &&
      balanceQuery.isSuccess &&
      workflowQuery.isSuccess &&
      canCalculateBalances &&
      !recalculationPending;
    if (
      initialBalanceCalculationGuard.claim({
        month,
        ready: readyForInitialCalculation,
        hasCurrentRun: Boolean(latestBalanceRun),
      })
    ) {
      calculateBalanceMutation.mutate();
    }
  }, [
    activeWorkflowStageId,
    balanceQuery.isSuccess,
    canCalculateBalances,
    latestBalanceRun,
    month,
    recalculationPending,
    workflowQuery.isSuccess,
    periodSummaryOnly,
  ]);

  useEffect(() => {
    const syncAuditOverlayState = () =>
      setPayrollAuditOverlayOpen(
        Boolean(readPayrollSearchParams().get("auditOverlay")),
      );
    window.addEventListener("popstate", syncAuditOverlayState);
    window.addEventListener(
      "payroll-audit-overlay-change",
      syncAuditOverlayState,
    );
    return () => {
      window.removeEventListener("popstate", syncAuditOverlayState);
      window.removeEventListener(
        "payroll-audit-overlay-change",
        syncAuditOverlayState,
      );
    };
  }, []);

  useEffect(() => {
    setSelectedYearEmployee(null);
    setEmployeeAuditOpen(false);
    setValueCorrectionTarget(null);
  }, [periodThroughMonth]);

  useEffect(() => {
    if (!selectedYearEmployee?.userId) return;
    const refreshedEmployee = (
      periodSummaryQuery.data?.employees as unknown as
        | YearSummaryEmployee[]
        | undefined
    )?.find((employee) => employee.userId === selectedYearEmployee.userId);
    if (refreshedEmployee && refreshedEmployee !== selectedYearEmployee)
      setSelectedYearEmployee(refreshedEmployee);
  }, [periodSummaryQuery.data, selectedYearEmployee]);

  useEffect(() => {
    if (
      !confirmation &&
      !payslipSheet &&
      !pohodaWorkbookPreviewId &&
      !monthChangeLogPreviewId &&
      !payrollAuditOverlayOpen &&
      !employeeAuditOpen &&
      !valueCorrectionTarget &&
      !selectedYearEmployee
    )
      return;
    const closeTopDialog = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (payrollAuditOverlayOpen) return;
      if (valueCorrectionTarget) {
        const trigger = valueCorrectionTarget.trigger;
        setValueCorrectionTarget(null);
        window.requestAnimationFrame(() => trigger?.focus());
      } else if (confirmation) setConfirmation(null);
      else if (employeeAuditOpen) setEmployeeAuditOpen(false);
      else if (payslipSheet) setPayslipSheet(null);
      else if (pohodaWorkbookPreviewId) setPohodaWorkbookPreviewId(null);
      else if (monthChangeLogPreviewId) setMonthChangeLogPreviewId(null);
      else if (selectedYearEmployee) setSelectedYearEmployee(null);
    };
    document.addEventListener("keydown", closeTopDialog);
    return () => document.removeEventListener("keydown", closeTopDialog);
  }, [
    confirmation,
    employeeAuditOpen,
    payslipSheet,
    pohodaWorkbookPreviewId,
    monthChangeLogPreviewId,
    selectedYearEmployee,
    payrollAuditOverlayOpen,
    valueCorrectionTarget,
  ]);

  const employeeForPayslipReview = (
    record: PayslipRecord,
  ):
    | AdminUser
    | {
        id: number;
        displayName: string;
        email: string;
        payrollEmployeeCode: string;
        payrollEligibleForMonth?: boolean;
      }
    | null => {
    const comparableName = (value: unknown) =>
      String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const employeeId = String(record.employee?.id ?? "");
    const employeeCode =
      record.employee?.payrollEmployeeCode || record.parsedPayrollCode;
    const exactEmployee = activePayrollUsers.find(
      (user) => String(user.id) === employeeId,
    );
    if (exactEmployee) return exactEmployee;
    if (employeeCode) {
      const codedEmployees = activePayrollUsers.filter(
        (user) =>
          user.payrollEmployeeCode?.trim().toLocaleLowerCase() ===
          employeeCode.trim().toLocaleLowerCase(),
      );
      return codedEmployees.length === 1
        ? (codedEmployees[0] as AdminUser)
        : null;
    }
    const parsedName = comparableName(record.parsedEmployeeName);
    if (!parsedName) return record.employee ?? null;
    const namedEmployees = activePayrollUsers.filter(
      (user) => comparableName(user.displayName) === parsedName,
    );
    return namedEmployees.length === 1
      ? (namedEmployees[0] as AdminUser)
      : (record.employee ?? null);
  };
  const defaultPayslipResolveDraft = (
    record: PayslipRecord,
  ): PayslipResolveDraft => {
    const employee = employeeForPayslipReview(record);
    return {
      employeeId: employee ? String(employee.id) : "",
      reason: "",
      replaceExisting: record.matchStatus === "duplicate",
    };
  };
  const updatePayslipResolveDraft = (
    record: PayslipRecord,
    patch: Partial<PayslipResolveDraft>,
  ) => {
    setPayslipResolveDrafts((drafts) => {
      const existing = drafts[record.id] ?? defaultPayslipResolveDraft(record);
      return { ...drafts, [record.id]: { ...existing, ...patch } };
    });
  };
  const resolvePayslip = (record: PayslipRecord) => {
    const draft =
      payslipResolveDrafts[record.id] ?? defaultPayslipResolveDraft(record);
    const employeeId = draft.employeeId.trim();
    if (!employeeId || !auditReasonIsValid(draft.reason)) return;
    resolvePayslipMutation.mutate({
      id: record.id,
      employeeId,
      reason: trimmedAuditReason(draft.reason),
      replaceExisting: draft.replaceExisting,
    });
  };
  const requestPayslipRemoval = (record: PayslipRecord) => {
    const draft =
      payslipResolveDrafts[record.id] ?? defaultPayslipResolveDraft(record);
    if (!auditReasonIsValid(draft.reason)) return;
    const reason = trimmedAuditReason(draft.reason);
    const reviewEmployee = employeeForPayslipReview(record);
    const requiresReplacement = payslipDiscardRequiresReplacement(
      record,
      payslipRecords,
      reviewEmployee?.payrollEligibleForMonth,
    );
    setConfirmation({
      title: "Discard this payslip?",
      description: payslipDiscardConfirmationDescription(requiresReplacement),
      confirmLabel: "Discard payslip",
      tone: "danger",
      onConfirm: () => deletePayslipMutation.mutate({ record, reason }),
    });
  };
  const showBalanceResult = (resultId: number) => {
    setCalculationDetailsOpen(true);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const candidates = Array.from(
          document.querySelectorAll<HTMLElement>(
            `[data-balance-result-id="${resultId}"]`,
          ),
        );
        const row =
          candidates.find((candidate) => candidate.getClientRects().length) ??
          candidates[0];
        row?.scrollIntoView({ behavior: "smooth", block: "center" });
        row?.focus({ preventScroll: true });
      });
    });
  };
  const focusPayslipBlocker = (requestedIndex: number) => {
    const nextIndex = Math.max(
      0,
      Math.min(exceptionPayslipRecords.length - 1, requestedIndex),
    );
    const nextRecord = exceptionPayslipRecords[nextIndex];
    setPayslipQueueIndex(nextIndex);
    window.requestAnimationFrame(() => {
      const matchedEmployee = nextRecord
        ? employeeForPayslipReview(nextRecord)
        : null;
      const target = nextRecord
        ? document.getElementById(
            matchedEmployee
              ? `payslip-reason-${nextRecord.id}`
              : `payslip-profile-fix-${nextRecord.id}`,
          )
        : document.getElementById("payslip-needs-attention");
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const employeeOverviewRows = monthlyPayrollUsers
    .map((user, index) => {
      const timesheetQuery = timesheetVersionQueries[index];
      const timesheet = timesheetStateByUserId.get(user.id) ?? {
        locked: false,
      };
      const timesheetLoading = Boolean(timesheetQuery?.isPending);
      const timesheetError = Boolean(timesheetQuery?.isError);
      const pohodaEmployee = pohodaPreview?.employees?.find(
        (employee) => employee.employeeId === user.id,
      );
      const employeePayslips = payslipRecords.filter(
        (record) => employeeForPayslipReview(record)?.id === user.id,
      );
      const activePayslip =
        employeePayslips.find(
          (record) => record.usable && record.calculationReady,
        ) ??
        employeePayslips.find((record) => record.matchStatus !== "replaced") ??
        null;
      const calculation =
        latestBalanceRun?.results.find(
          (result) => result.profileId === user.id,
        ) ?? null;
      let requiredAction = "Complete";
      let actionPriority = 70;
      if (timesheetLoading) {
        requiredAction = "Checking lock state";
        actionPriority = -1;
      } else if (timesheetError) {
        requiredAction = "Retry lock check";
        actionPriority = -1;
      } else if (!timesheet.locked && timesheet.available === false) {
        requiredAction = "No OpenProject time entries for this month";
        actionPriority = 0;
      } else if (!timesheet.locked) {
        requiredAction = "Lock timesheet";
        actionPriority = 0;
      } else if (!pohodaEmployee?.ready) {
        requiredAction = "Complete POHODA input";
        actionPriority = 10;
      } else if (!activePayslip) {
        requiredAction = "Upload payslip";
        actionPriority = 20;
      } else if (!activePayslip.usable || !activePayslip.calculationReady) {
        requiredAction = "Resolve payslip";
        actionPriority = 5;
      } else if (!calculation) {
        requiredAction = "Await automatic balance check";
        actionPriority = 30;
      } else if (calculation.statusColor === "red") {
        requiredAction = "Resolve differences";
        actionPriority = 4;
      } else if (!calculation.approved) {
        requiredAction = "Approve calculation";
        actionPriority = 40;
      }
      return {
        user,
        timesheet,
        timesheetLoading,
        timesheetError,
        pohodaEmployee,
        activePayslip,
        calculation,
        requiredAction,
        actionPriority,
      };
    })
    .sort(
      (left, right) =>
        left.actionPriority - right.actionPriority ||
        (left.user.displayName || left.user.username).localeCompare(
          right.user.displayName || right.user.username,
        ),
    );
  const payslipConfirmationRows = employeeOverviewRows
    .map((row) => {
      const record = row.activePayslip;
      const eligibleForPayslip = row.user.payrollEligibleForMonth !== false;
      const needsAttention = Boolean(record && payslipNeedsAttention(record));
      const status = !eligibleForPayslip
        ? "Not eligible this month"
        : !record
          ? "No payslip"
          : needsAttention
            ? "Needs checking"
            : record.calculationReady
              ? "Confirmed"
              : "Ready to confirm";
      return {
        row,
        record,
        eligibleForPayslip,
        needsAttention,
        status,
        category: payslipReviewCategoryForStatus(status),
      };
    })
    .sort(
      (left, right) =>
        payslipReviewCategoryOrder[left.category] -
          payslipReviewCategoryOrder[right.category] ||
        left.row.user.displayName.localeCompare(right.row.user.displayName),
    );
  const filteredPayslipConfirmationRows = payslipConfirmationRows.filter(
    ({ category }) =>
      payslipListFilter === "all" || category === payslipListFilter,
  );
  const payslipConfirmationAttentionCount = payslipConfirmationRows.filter(
    ({ category }) => category === "attention",
  ).length;
  const missingPayslipEmployeeCount = payslipConfirmationRows.filter(
    ({ record, eligibleForPayslip }) => eligibleForPayslip && !record,
  ).length;
  const requestBulkPayslipConfirmation = () => {
    if (monthClosed || readyToConfirmPayslipEmployeeCount === 0) return;
    setConfirmation({
      title: `Confirm ${readyToConfirmPayslipEmployeeCount} ready ${readyToConfirmPayslipEmployeeCount === 1 ? "payslip" : "payslips"}?`,
      description: `Only payslips that passed every automated check will be confirmed.${payslipConfirmationAttentionCount > 0 ? ` ${payslipConfirmationAttentionCount} ${payslipConfirmationAttentionCount === 1 ? "record is" : "records are"} excluded and will stay in Needs attention.` : ""}`,
      confirmLabel: `Confirm ${readyToConfirmPayslipEmployeeCount}`,
      checkpointTitle: "Needs-attention records are excluded",
      checkpointDescription:
        "The server checks employee match, payroll month, document safety, and review blockers again before saving.",
      onConfirm: () => bulkConfirmPayslipsMutation.mutate(),
    });
  };
  const requestRemoveWrongMonthPayslips = () => {
    if (
      !removableWrongMonthRecords.length ||
      monthClosed ||
      removeWrongMonthPayslipsMutation.isPending
    )
      return;
    const records = [...removableWrongMonthRecords];
    setConfirmation({
      title: `Remove ${records.length} wrong-month payslip${records.length === 1 ? "" : "s"}?`,
      description: `These PDFs identify ${wrongMonthUploadPeriods.map(monthLabel).join(", ")}, but were uploaded into ${monthLabel(month)}. They will be removed from this month's review so you can upload the correct files.`,
      confirmLabel: `Remove ${records.length} payslip${records.length === 1 ? "" : "s"}`,
      tone: "danger",
      checkpointTitle: "Original PDFs and audit history are preserved",
      checkpointDescription:
        "Only unconfirmed, uncorrected wrong-month records from this upload are removed. Existing payslips in other months stay unchanged.",
      onConfirm: () =>
        removeWrongMonthPayslipsMutation.mutate({ records, sourceMonth: month }),
    });
  };
  const requestKeepExistingPayslips = () => {
    if (!payslipActionBatch || latestSameMonthDuplicateRecords.length === 0)
      return;
    const duplicateCount = latestSameMonthDuplicateRecords.length;
    setConfirmation({
      title: `Keep ${duplicateCount} existing ${duplicateCount === 1 ? "payslip" : "payslips"}?`,
      description: `The repeated upload will be removed from review in one action. The ${duplicateCount} payslip${duplicateCount === 1 ? "" : "s"} already stored for ${monthLabel(month)} will remain unchanged.`,
      confirmLabel: "Keep existing payslips",
      checkpointTitle: "No existing payroll values will change",
      checkpointDescription:
        "The action is recorded once in the payroll audit with the account, month, time, and number of affected employees.",
      onConfirm: () =>
        keepExistingPayslipsMutation.mutate(payslipActionBatch.id),
    });
  };
  const reviewProtectedCrossMonthPayslip = () => {
    const record = latestProtectedCrossMonthDuplicateRecords[0];
    if (!record) return;
    setPayslipListFilter("attention");
    openPayslipSheet(record);
  };
  const requestCrossMonthPayslipRecovery = () => {
    if (!payslipActionBatch || latestCrossMonthDuplicateRecords.length === 0)
      return;
    const duplicateCount = latestCrossMonthDuplicateRecords.length;
    const sourceMonthText = latestCrossMonthSourceMonths
      .map(monthLabel)
      .join(", ");
    setConfirmation({
      title: `Use this upload for ${monthLabel(month)}?`,
      description: `${duplicateCount} ${duplicateCount === 1 ? "payslip matches" : "payslips match"} ${monthLabel(month)}, but the same PDF was previously stored under ${sourceMonthText || "another payroll month"}. The correct records will become ready to confirm here and the wrong-month review records will be removed.`,
      confirmLabel: `Use for ${monthLabel(month)}`,
      checkpointTitle: "Employee links and PDFs stay unchanged",
      checkpointDescription:
        "Only the payroll-month assignment changes. One audit entry records the source month, destination month, account, time, and affected payslips.",
      onConfirm: () =>
        recoverCrossMonthPayslipsMutation.mutate(payslipActionBatch.id),
    });
  };
  const normalizedEmployeeSearch = employeeSearch.trim().toLocaleLowerCase();
  const attentionEmployeeCount = employeeOverviewRows.filter(
    (row) => row.requiredAction !== "Complete",
  ).length;
  const completeEmployeeCount =
    employeeOverviewRows.length - attentionEmployeeCount;
  const filteredEmployeeOverviewRows = employeeOverviewRows.filter((row) => {
    const matchesSearch =
      !normalizedEmployeeSearch ||
      [
        row.user.displayName,
        row.user.username,
        row.user.email,
        row.user.payrollEmployeeCode,
        row.requiredAction,
      ].some((value) =>
        String(value ?? "")
          .toLocaleLowerCase()
          .includes(normalizedEmployeeSearch),
      );
    const matchesStatus =
      employeeStatusFilter === "all" ||
      (employeeStatusFilter === "attention" &&
        row.requiredAction !== "Complete") ||
      (employeeStatusFilter === "complete" &&
        row.requiredAction === "Complete");
    return matchesSearch && matchesStatus;
  });
  const requestTimesheetToggle = (
    row: (typeof employeeOverviewRows)[number],
  ) => {
    const employeeName = row.user.displayName || row.user.username;
    if (row.timesheet.locked) {
      setConfirmation({
        title: `Unlock ${employeeName}'s timesheet?`,
        description: `This releases the finalized ${monthLabel(month)} timesheet so it can be corrected. Existing payroll artifacts are retained, but should be regenerated after the timesheet is locked again.`,
        confirmLabel: "Unlock timesheet",
        tone: "danger",
        onConfirm: () =>
          rowLockMutation.mutate({
            employeeId: row.user.id,
            employeeName,
            action: "unlock",
          }),
      });
      return;
    }
    setConfirmation({
      title: `Lock ${employeeName}'s timesheet?`,
      description: `Freeze the synchronized OpenProject time entries for ${monthLabel(month)} as an immutable PayDay payroll snapshot. OpenProject is not changed.`,
      confirmLabel: "Lock timesheet",
      onConfirm: () =>
        rowLockMutation.mutate({
          employeeId: row.user.id,
          employeeName,
          action: "lock",
        }),
    });
  };

  const requestPohodaGeneration = () => pohodaExportMutation.mutate();

  const requestPayslipReplacement = (employeeName: string) => {
    if (payslipUploadPendingRef.current) return;
    setConfirmation({
      title: `Replace ${employeeName}'s payslip?`,
      description:
        "A valid PDF for this employee and month replaces the latest payslip automatically. The previous version stays in history. Confirm the replacement, then recalculate balances and review approval again.",
      confirmLabel: "Choose replacement PDF",
      tone: "danger",
      onConfirm: () => {
        if (payslipUploadPendingRef.current) return;
        setReplacementEmployeeName(employeeName);
        replacementFileInputRef.current?.click();
      },
    });
  };

  const requestMissingPayslipUpload = () => {
    if (payslipUploadPendingRef.current) return;
    setSelectedWorkflowStage("payslips");
    setPayslipSubstep("upload");
    setReplacementEmployeeName("");
    replacementFileInputRef.current?.click();
  };

  const showEmployeePeriodHistory = (userId: string | number) => {
    const summary = yearSummaryRows.find((row) => row.userId === userId);
    if (!summary) {
      setFeedback({
        tone: "info",
        message: "Period history is not available for this employee yet.",
      });
      return;
    }
    setSelectedYearEmployee(summary);
    setShowEmptyPeriodMonths(false);
    setEmployeeAuditOpen(false);
  };
  if (payrollAccessQuery.isLoading) {
    return (
      <Panel>
        <p className="text-sm text-foreground-muted">
          Checking OpenProject payroll access...
        </p>
      </Panel>
    );
  }

  if (!hasPayrollAccess) {
    return (
      <Panel>
        <p className="text-sm text-danger">
          OpenProject payroll access is required for this page.
        </p>
      </Panel>
    );
  }

  const workflowNextDisabledReason = payrollNextDisabledReason(
    activeWorkflowStage,
    isWorkflowRefreshing,
  );
  const nextDisabledReason =
    activeWorkflowStage.id === "payslips" &&
    latestCrossMonthDuplicateRecords.length > 0
      ? `Use this upload for ${monthLabel(month)} above, then confirm the recovered payslips.`
      : workflowNextDisabledReason;
  const nextHelpId = `payroll-next-help-${activeWorkflowStage.id}`;
  const workflowActionFooter =
    activeWorkflowStage.id === recommendedStage.id ? (
      <div
        data-workflow-footer-placement="bottom-right"
        className={`flex shrink-0 flex-wrap items-center justify-end gap-2 px-3 py-2 ${activeWorkflowStage.id === "close" ? "bg-elevated" : "mt-auto border-t border-border"}`}
      >
        {activeWorkflowStage.id !== "close" && nextDisabledReason && (
          <p
            id={nextHelpId}
            role="status"
            className="mr-auto max-w-2xl text-sm text-foreground-muted"
          >
            {activeWorkflowStage.id === "reconcile"
              ? staleCalculationCount > 0 ? "Recalculate the changed employee rows before approval." : nextDisabledReason
              : `Next is disabled. ${nextDisabledReason}`}
          </p>
        )}
        {activeWorkflowStage.id === "reconcile" &&
          latestBalanceRun &&
          approvalBlockingCheckCount === 0 &&
          latestBalanceRun.status !== "approved" && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              disabled={
                approveBalanceMutation.isPending ||
                isWorkflowRefreshing ||
                !canApproveCalculation
              }
              onClick={() => approveBalanceMutation.mutate({
                id: latestBalanceRun.id,
                revision: latestBalanceRun.revision,
                reason: "Approved from guided payroll workflow",
              })}
            >
              <ShieldCheck size={16} aria-hidden="true" />
              {approveBalanceMutation.isPending
                ? "Approving…"
                : "Approve payroll"}
            </Button>
          )}
        {activeWorkflowStage.id === "reconcile" &&
          latestBalanceRun?.status === "approved" && (
            <span
              role="status"
              className="inline-flex min-h-11 items-center gap-2 px-3 text-sm font-semibold text-success"
            >
              <Check size={17} aria-hidden="true" />
              Approved
            </span>
          )}
        {activeWorkflowStage.id === "close" ? (
          <>
            <PayrollEmailMenu
              disabled={(!monthClosed && !canReviewCompletion) || isWorkflowRefreshing || sendPayrollEmailsMutation.isPending || emailPreviewQuery.isLoading}
              sending={sendPayrollEmailsMutation.isPending}
              onChoose={(mode) => setConfirmation(emailConfirmation(mode))}
            />
            {!monthClosed && (
              <Button
                type="button"
                size="md"
                variant="outline"
                className="min-h-11 border-accent/30 bg-accent-subtle text-accent"
                onClick={() =>
                  setConfirmation({
                    title: `Close ${monthLabel(month)} payroll?`,
                    description:
                      "This records the actor, time, current input versions, and checksums, then publishes the month to Period Summary.",
                    confirmLabel: "Close and publish month",
                    onConfirm: () => closeMonthMutation.mutate(),
                  })
                }
                disabled={
                  !canReviewCompletion ||
                  isWorkflowRefreshing ||
                  closeMonthMutation.isPending
                }
              >
                <ClipboardCheck size={16} aria-hidden="true" />
                {closeMonthMutation.isPending
                  ? "Closing…"
                  : "Close month"}
              </Button>
            )}
            {monthClosed && (
              <>
                <Link
                  to="/period-summary"
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-4 text-sm font-semibold text-foreground hover:bg-elevated"
                >
                  <ClipboardCheck size={16} aria-hidden="true" />
                  Review period summary
                </Link>
              </>
            )}
            {emailSentCount !== null && emailSentCount > 0 && (
              <span
                role="status"
                className="text-sm font-medium text-muted-foreground"
              >
                {emailSentCount} payroll email
                {emailSentCount === 1 ? "" : "s"} previously sent.
              </span>
            )}
            {emailSimulatedCount !== null && emailSimulatedCount > 0 && (
              <span
                role="status"
                className="text-sm font-medium text-muted-foreground"
              >
                {emailSimulatedCount} payroll email
                {emailSimulatedCount === 1 ? "" : "s"} simulated (not sent).
              </span>
            )}
            {emailSendResult && (
              <span
                role="status"
                className="text-sm font-medium text-muted-foreground"
              >
                {emailSendResult}
              </span>
            )}
          </>
        ) : (
          <Button
            type="button"
            size="lg"
            disabled={
              !activeWorkflowStage.canAdvance ||
              isWorkflowRefreshing ||
              advanceWorkflowMutation.isPending
            }
            aria-describedby={nextDisabledReason ? nextHelpId : undefined}
            onClick={() =>
              advanceWorkflowMutation.mutate(
                activeWorkflowStage.id as Exclude<
                  PayrollWorkflowStageId,
                  "close"
                >,
              )
            }
          >
            {payrollNextLabel(
              workflowStages,
              activeWorkflowStage.id,
              advanceWorkflowMutation.isPending,
            )}
            <ArrowRight size={17} aria-hidden="true" />
          </Button>
        )}
      </div>
    ) : null;

  return (
    <div
      className={`payroll-ledger-page ${periodSummaryOnly ? "space-y-5" : "space-y-3"}`}
      data-payroll-layout="fiori-floorplan"
      data-active-stage={periodSummaryOnly ? "summary" : activeWorkflowStage.id}
      data-evidence-mode="contextual"

    >
      {!periodSummaryOnly && (
        <>
          <Panel elevated className="payroll-context-panel shrink-0 overflow-hidden !p-0">
            <div className="payroll-context-toolbar flex flex-wrap items-center gap-2 px-3 py-2">
              <div className="payroll-page-landmark payroll-page-landmark--payroll mr-auto min-w-0">
                <FileText size={18} aria-hidden="true" className="text-foreground-muted" />
                <h1 className="text-base font-semibold text-foreground">Monthly payroll</h1>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <MonthYearPicker value={month} onChange={setMonth} monthStatuses={monthStatuses} />
                <span role="status" aria-label="Payroll month status" className={`${monthStatus === "open" || monthStatus === "closed" ? "sr-only" : ""} text-xs font-medium text-foreground-muted`}>
                  {monthStatus === "closed" ? "Closed · read-only" : monthStatus === "open" ? "Open" : monthStatus === "checking" ? "Checking status…" : monthStatus === "unavailable" ? "Status unavailable" : "Status unknown"}
                </span>
              </div>
              {workflowStateUnavailable && <span role="status" aria-live="polite">{failedWorkflowDataQuery ? "Workflow unavailable" : "Loading workflow"}</span>}
            </div>
            <ActionFeedback feedback={feedback} onDismiss={() => setFeedback(null)} />
            {failedPayrollDataQuery && (
              <div
                role="alert"
                className="mx-3 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/40 bg-danger-subtle px-3 py-2 text-sm text-danger"
              >
                <span>
                  {failedPayrollDataQuery.label} could not be loaded.{" "}
                  {payrollLoadErrorMessage(failedPayrollDataQuery.query.error)}
                  {failedPayrollDataQuery.label === "Saved Pohoda versions"
                    ? " You can continue with the current workflow state."
                    : ""}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="border-danger/40 text-danger hover:bg-danger/10"
                  onClick={() => {
                    void failedPayrollDataQuery.query.refetch();
                  }}
                >
                  Retry payroll data
                </Button>
              </div>
            )}
          </Panel>

          <Panel className="payroll-workflow-panel overflow-hidden rounded-xl !p-0">
            <div className="payroll-stage-layout">
              <div className="bg-elevated py-1">
                <PayrollWorkflowRail
                  stages={workflowStages}
                  selectedId={activeWorkflowStageId}
                  recommendedId={recommendedStage.id}
                  onSelect={selectWorkflowStage}
                />
              </div>
              <div className="payroll-stage-workspace relative flex min-w-0 flex-col bg-surface">
                <div className="payroll-stage-command min-w-0 px-3 py-2">
                  <div
                    className={
                      activeWorkflowStage.id === "payslips"
                        ? "hidden"
                        : "flex flex-wrap items-start justify-between gap-4"
                    }
                  >
                    <div
                      className={
                        activeWorkflowStage.id === "payslips"
                          ? "min-w-0"
                          : "min-w-0 max-w-3xl"
                      }
                    >
                      {activeWorkflowStage.id !== "prepare" && <div className="relative flex items-center gap-2">
                        <h3
                          ref={
                            activeWorkflowStage.id === "payslips"
                              ? undefined
                              : workflowHeadingRef
                          }
                          tabIndex={-1}
                          className={activeWorkflowStage.id === "reconcile" ? "sr-only" : "scroll-mt-6 text-base font-semibold outline-none"}
                        >
                          {activeWorkflowStage.title}
                        </h3>
                      </div>}
                      {!["prepare", "close", "reconcile"].includes(activeWorkflowStage.id) && (!["pohoda", "payslips"].includes(
                        activeWorkflowStage.id,
                      ) || activeWorkflowStage.state !== "completed") ? (
                        <p className="mt-1 text-sm leading-5 text-foreground-muted">
                          {activeWorkflowStage.summary}
                        </p>
                      ) : null}
                      {activeWorkflowStage.blocker &&
                        activeWorkflowStage.state !== "completed" && (
                          <p className="mt-2 flex items-start gap-2 text-sm font-medium">
                            <AlertTriangle
                              size={16}
                              className="mt-0.5 shrink-0"
                              aria-hidden="true"
                            />
                            {activeWorkflowStage.blocker}
                          </p>
                        )}
                    </div>
                    {!workflowStateUnavailable && (
                      <div
                        className={`payroll-stage-actions ${
                          activeWorkflowStage.id === "payslips"
                            ? "min-w-0"
                            : activeWorkflowStage.id === "close" && monthClosed
                              ? "mt-1 flex w-full basis-full flex-wrap items-center justify-end gap-2"
                              : "flex flex-wrap items-center justify-end gap-2"
                        }`}
                      >
                        {activeWorkflowStage.state === "completed" &&
                          (monthClosed ||
                            (activeWorkflowStage.id === recommendedStage.id &&
                              activeWorkflowStage.id !== "close")) && (
                            <Button
                              ref={reopenTriggerRef}
                              type="button"
                              variant="ghost"
                              size="lg"
                              onClick={() => {
                                setReopenStage(activeWorkflowStage.id);
                                setReopenReason("");
                              }}
                              className="text-warning hover:bg-warning-subtle hover:text-warning"
                            >
                              <RotateCcw size={16} aria-hidden="true" />
                              Reopen from this stage
                            </Button>
                          )}
                        {activeWorkflowStage.id === "reconcile" &&
                          automaticBalanceCalculationPending && (
                            <span
                              role="status"
                              aria-live="polite"
                              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-accent/35 bg-accent-subtle px-3 text-sm font-semibold text-foreground"
                            >
                              <CircleDotDashed
                                size={16}
                                className="animate-spin text-accent"
                                aria-hidden="true"
                              />
                              Calculating balances automatically…
                            </span>
                          )}
                        {manualRecalculationAvailable && (
                          <Button
                            type="button"
                            onClick={requestBalanceCalculation}
                            disabled={
                              calculateBalanceMutation.isPending ||
                              calculateBalanceRowMutation.isPending ||
                              !canCalculateBalances
                            }
                            size="lg"
                          >
                            <RotateCcw size={16} aria-hidden="true" />
                            {calculateBalanceMutation.isPending
                              ? "Calculating…"
                              : balanceCalculationFailure
                                ? "Retry calculation"
                                : "Recalculate balances"}
                          </Button>
                        )}
                        {activeWorkflowStage.id === "close" && (
                          <>
                            {monthClosed && (
                              <PayrollCloseArtifacts
                                  versions={monthChangeLogs}
                                  loading={monthChangeLogsQuery.isLoading}
                                  downloading={viewMonthChangeLogMutation.isPending}
                                  onOpen={setMonthChangeLogPreviewId}
                                  onDownload={(id) => viewMonthChangeLogMutation.mutate(id)}
                                />
                            )}

                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {activeWorkflowStage.id === "reconcile" && (
                    <div
                      ref={setCalculationStageTarget}
                      className="min-w-0 lg:min-h-0 lg:flex-1"
                    />
                  )}
                  {activeWorkflowStage.id === "prepare" && (
                    <div
                      ref={setPrepareStageTarget}
                      className="min-w-0 lg:min-h-0 lg:flex-1"
                    />
                  )}
                  {activeWorkflowStage.id === "payslips" && (
                    <div className="payroll-payslip-workspace flex min-w-0 flex-col gap-3">
                      <section
                        className="payroll-payslip-content min-w-0"
                        aria-live="polite"
                      >
                        {(
                          <div>
                            <h4 ref={workflowHeadingRef} tabIndex={-1} className="text-sm font-semibold text-foreground">
                              Upload payslips
                            </h4>
                            <div className="mt-3">
                              <PayslipDropZone
                                canUpload={canUploadPayslips}
                                compact
                                files={payslipFiles}
                                dragActive={payslipDropActive}
                                uploading={uploadPayslipsMutation.isPending}
                                onDragActiveChange={setPayslipDropActive}
                                onFilesAdded={addPayslipFiles}
                                onRemove={(file) => {
                                  if (payslipUploadPendingRef.current) return;
                                  setPayslipFiles((currentFiles) =>
                                    currentFiles.filter(
                                      (candidate) => candidate !== file,
                                    ),
                                  );
                                }}
                                onUpload={() => {
                                  if (payslipUploadPendingRef.current) return;
                                  payslipUploadPendingRef.current = true;
                                  uploadPayslipsMutation.mutate({ month, files: payslipFiles });
                                }}
                              />
                            </div>
                            {latestPayslipFileResults.length > 0 ? (
                              <section
                                aria-label="Latest upload"
                                className="mt-4 border-t border-border pt-3"
                              >
                                {wrongMonthUploadRecords.length > 0 ? (
                                  <div
                                    role="alert"
                                    className="mt-3 rounded-lg border border-warning/40 bg-warning-subtle p-3 text-sm"
                                  >
                                    <p className="font-semibold text-foreground">
                                      {wrongMonthUploadPeriods.length === 1 &&
                                      wrongMonthUploadRecords.length ===
                                        activeLatestPayslipFileResults.length
                                        ? `This upload belongs to ${monthLabel(wrongMonthUploadPeriods[0]!)}.`
                                        : `${wrongMonthUploadRecords.length} payslips belong to a different payroll month.`}
                                      {` ${monthLabel(month)} is selected.`}
                                    </p>
                                    <p className="mt-1 text-foreground-muted">
                                      Remove the wrong-month payslips, then upload the correct PDFs for this
                                      month. You can also open the month shown in the PDFs.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {removableWrongMonthRecords.length > 0 ? (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="danger"
                                          loading={removeWrongMonthPayslipsMutation.isPending}
                                          disabled={
                                            monthClosed ||
                                            removeWrongMonthPayslipsMutation.isPending ||
                                            deletePayslipMutation.isPending
                                          }
                                          onClick={requestRemoveWrongMonthPayslips}
                                        >
                                          Remove wrong-month payslips
                                        </Button>
                                      ) : null}
                                      {wrongMonthUploadPeriods.map((period) => (
                                        <Button
                                          key={period}
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          disabled={removeWrongMonthPayslipsMutation.isPending}
                                          onClick={() => {
                                            setMonth(period);
                                            setPayslipSubstep("upload");
                                          }}
                                        >
                                          Open {monthLabel(period)}
                                        </Button>
                                      ))}
                                    </div>
                                    {removableWrongMonthRecords.length < wrongMonthUploadRecords.length ? (
                                      <p className="mt-2 text-foreground-muted">
                                        Confirmed or corrected payslips require individual review.
                                      </p>
                                    ) : null}
                                  </div>
                                ) : null}
                                {latestCrossMonthDuplicateRecords.length > 0 ||
                                latestSameMonthDuplicateRecords.length > 0 ||
                                latestProtectedCrossMonthDuplicateRecords.length >
                                  0 ? (
                                  <div
                                    className="mt-3 flex flex-wrap gap-2"
                                    aria-label="Latest upload actions"
                                  >
                                    {latestCrossMonthDuplicateRecords.length >
                                    0 ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        loading={
                                          recoverCrossMonthPayslipsMutation.isPending
                                        }
                                        disabled={monthClosed}
                                        onClick={
                                          requestCrossMonthPayslipRecovery
                                        }
                                      >
                                        <RotateCcw
                                          size={15}
                                          aria-hidden="true"
                                        />
                                        Use for {monthLabel(month)}
                                      </Button>
                                    ) : null}
                                    {latestSameMonthDuplicateRecords.length >
                                    0 ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        loading={
                                          keepExistingPayslipsMutation.isPending
                                        }
                                        disabled={monthClosed}
                                        onClick={requestKeepExistingPayslips}
                                      >
                                        <ShieldCheck
                                          size={15}
                                          aria-hidden="true"
                                        />
                                        Keep existing payslips
                                      </Button>
                                    ) : null}
                                    {latestProtectedCrossMonthDuplicateRecords.length >
                                    0 ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="warning"
                                        onClick={
                                          reviewProtectedCrossMonthPayslip
                                        }
                                      >
                                        <Eye size={15} aria-hidden="true" />
                                        Review individually
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : null}
                                <details className="mt-4 border-t border-border pt-3" aria-label="Upload history">
                                  <summary className="cursor-pointer text-sm font-medium text-accent">Latest upload · {latestPayslipFileResults.length} results</summary>
                                  <p className="mt-1 text-xs text-foreground-muted">Upload results: {latestPayslipUploadCounts.accepted} accepted · {latestPayslipUploadCounts.review} need review{latestPayslipUploadCounts.notInPayroll ? ` · ${latestPayslipUploadCounts.notInPayroll} not in this payroll` : ""}{latestPayslipUploadCounts.replaced ? ` · ${latestPayslipUploadCounts.replaced} replaced` : ""}{latestPayslipUploadCounts.removed ? ` · ${latestPayslipUploadCounts.removed} removed` : ""}</p>
                                <ul
                                  className="mt-2 divide-y divide-border"
                                  aria-label="Latest upload file outcomes"
                                >
                                  {latestPayslipRows.map(
                                    ({ result, record, status, category }) => (
                                      <li
                                        key={`${result.fileName}-${result.recordId ?? "none"}`}
                                        className="payroll-upload-history-row"
                                      >
                                        <span
                                          className="payroll-upload-history-file text-sm font-medium text-foreground"
                                        >
                                          {result.fileName}
                                          <span className="mt-1 block text-sm font-normal text-foreground-muted">
                                            {record?.employee ? `Mapped to: ${record.employee.displayName}` : result.employeeName ? `${result.employeeId ? "Mapped to" : "Employee"}: ${result.employeeName}` : record?.parsedEmployeeName ? `Not mapped · PDF name: ${record.parsedEmployeeName}` : "Employee not identified"}
                                            {record?.sectionCount && record.sectionCount > 1 ? ` · Payslip ${(record.sectionIndex ?? 0) + 1} of ${record.sectionCount}` : ""}
                                          </span>
                                        </span>
                                        <StatusChip
                                          tone={
                                            category === "attention"
                                              ? "warning"
                                              : category === "ready"
                                                ? "info"
                                                : "success"
                                          }
                                          variant="outline"
                                        >
                                          {result.code === "payslip_replaced"
                                            ? `Replacement · ${status}`
                                            : status}
                                        </StatusChip>
                                        <span className="payroll-upload-history-detail text-sm leading-5 text-foreground-muted">
                                          {record?.calculationReady ? null : !record && result.recordId ? "Earlier upload result" : record?.matchStatus === "ineligible" ? "Employee excluded from this month" : payslipUploadResultMessage(result.code, result.message)}
                                        </span>
                                        <span className="payroll-upload-history-action">
                                          {record && record.matchStatus !== "ignored" && !record.replacedByRecordId ? <Button type="button" size="sm" variant="ghost" disabled={monthClosed || removeWrongMonthPayslipsMutation.isPending} onClick={() => { setUploadRemovalRecord(record); setUploadRemovalReason(""); }} aria-label={`Remove ${result.fileName} for ${record.employee?.displayName || record.parsedEmployeeName || "unmapped employee"}`}>Remove payslip</Button> : null}
                                        </span>
                                      </li>
                                    ),
                                  )}
                                </ul>
                                </details>
                              </section>
                            ) : null}
                          </div>
                        )}
                        {(
                          <div className="payroll-payslip-panel isolate mt-3 flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-surface">
                            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-3 py-2">
                              <div>
                                <h4 className="text-sm font-semibold text-foreground">
                                  Confirm &amp; finish
                                </h4>
                                <p className="mt-0.5 text-xs text-foreground-muted">
                                  {authoritativePayslipProgress.confirmedCount}{" "}
                                  of{" "}
                                  {authoritativePayslipProgress.eligibleCount}{" "}
                                  employees confirmed
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {readyToConfirmPayslipEmployeeCount > 0 ? (
                                  <Button type="button" loading={bulkConfirmPayslipsMutation.isPending} disabled={monthClosed} onClick={requestBulkPayslipConfirmation}>
                                    <Check size={17} aria-hidden="true" />
                                    Confirm all {readyToConfirmPayslipEmployeeCount} and review
                                  </Button>
                                ) : null}
                                {missingPayslipEmployeeCount > 0 ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={requestMissingPayslipUpload}
                                  >
                                    <Upload size={15} aria-hidden="true" />
                                    Upload missing payslips
                                  </Button>
                                ) : null}
                                <select aria-label="Filter employees to confirm" value={payslipListFilter} onChange={(event) => setPayslipListFilter(event.target.value as PayslipListFilter)} className="min-h-11 rounded-lg border border-border bg-surface px-2 text-xs">
                                  <option value="all">All employees</option><option value="attention">Needs attention</option><option value="ready">Ready</option><option value="complete">Completed</option>
                                </select>
                              </div>
                            </div>
                            <div
                              className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                              tabIndex={0}
                              aria-label="Scrollable employee confirmation list"
                            >
                              <div className="payroll-payslip-columns gap-4 border-t border-border bg-surface/60 px-4 py-2 text-xs font-semibold text-foreground-subtle">
                                <span>Employee</span>
                                <span>Source</span>
                                <span>Status</span>
                                <span className="pr-1 text-right">View</span>
                              </div>
                              <ul
                                className="divide-y divide-border border-t border-border lg:border-t-0"
                                aria-label="Employees to confirm"
                              >
                                {filteredPayslipConfirmationRows.map(
                                  (
                                    {
                                      row,
                                      record,
                                      eligibleForPayslip,
                                      needsAttention,
                                      status,
                                      category,
                                    },
                                    index,
                                  ) => {
                                    const showCategoryHeading = category === "attention" && (index === 0 || filteredPayslipConfirmationRows[index - 1]?.category !== category);
                                    const categoryMeta =
                                      payslipReviewCategoryMeta[category];
                                    return (
                                      <Fragment key={row.user.id}>
                                        {showCategoryHeading ? (
                                          <li className="flex flex-wrap items-baseline justify-between gap-1 bg-surface/75 px-4 py-2">
                                            <span className="text-xs font-semibold text-foreground">
                                              {categoryMeta.title}
                                            </span>
                                            <span className="text-xs text-foreground-subtle">
                                              {categoryMeta.description}
                                            </span>
                                          </li>
                                        ) : null}
                                        <li
                                          className={`payroll-payslip-row grid items-center gap-x-3 gap-y-1 px-3 py-2 transition-colors duration-150 hover:bg-elevated/35 ${
                                            category === "attention"
                                              ? "bg-warning-subtle/10"
                                              : category === "ready"
                                                ? "bg-success-subtle/5"
                                                : ""
                                          }`}
                                        >
                                          <EmployeeIdentity name={row.user.displayName} />
                                          <div className="min-w-0">
                                            <p className="truncate text-xs font-medium text-foreground-muted">
                                              {record
                                                ? record.sourceFileName ||
                                                  "Payslip available"
                                                : eligibleForPayslip
                                                  ? "No current payslip"
                                                  : "Not eligible this month"}
                                            </p>
                                            {!record ? (
                                              <p
                                                className={`mt-0.5 text-xs ${
                                                  eligibleForPayslip
                                                    ? "text-danger"
                                                    : "text-foreground-subtle"
                                                }`}
                                              >
                                                {eligibleForPayslip
                                                  ? <>Upload or match a current payslip. <Link to={`/work-profiles/${row.user.id}`} search={{ resolve: "missing-payslip" }} state={{ payrollProfileRecovery: { month, periodThrough: periodThroughMonth, resolve: "missing-payslip" } }} className="underline">Check Work Profile</Link></>
                                                  : "No payslip is required for this period."}
                                              </p>
                                            ) : needsAttention ? (
                                              <p className="mt-0.5 text-xs text-warning">
                                                {
                                                  payslipIssueContext(record)
                                                    .title
                                                }
                                              </p>
                                            ) : (
                                              null
                                            )}
                                          </div>
                                          <div>
                                            <Badge
                                              variant={
                                                status === "Confirmed"
                                                  ? "success"
                                                  : status ===
                                                      "Ready to confirm"
                                                    ? "info"
                                                    : status ===
                                                        "Needs checking"
                                                      ? "warning"
                                                      : "default"
                                              }
                                              className="border border-current/30"
                                            >
                                              {status}
                                            </Badge>
                                          </div>
                                          <div className="payroll-payslip-review flex flex-wrap items-center gap-2">
                                            {record ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  openPayslipSheet(record)
                                                }
                                                aria-label={`View extracted payslip for ${row.user.displayName}`}
                                                title={`View extracted payslip for ${row.user.displayName}`}
                                                className="inline-flex size-11 items-center justify-center rounded-lg border border-border text-foreground-muted hover:bg-elevated hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                              >
                                                <Eye
                                                  size={17}
                                                  aria-hidden="true"
                                                />
                                              </button>
                                            ) : null}
                                          </div>
                                        </li>
                                      </Fragment>
                                    );
                                  },
                                )}
                              </ul>
                              {filteredPayslipConfirmationRows.length === 0 ? (
                                <p className="border-t border-border px-4 py-5 text-center text-sm text-foreground-muted">
                                  No employees match this filter.
                                </p>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {payslipsConfirmed && (
                          <div className="mt-3 flex justify-end"><Button type="button" variant="secondary" onClick={() => setSelectedWorkflowStage("reconcile")}>Continue to balances<ArrowRight size={17} aria-hidden="true" /></Button></div>
                        )}
                      </section>
                    </div>
                  )}
                  {activeWorkflowStage.id === "reconcile" &&
                    calculationReadiness &&
                    !calculationReadiness.ready && (
                      <div className="mt-4 rounded-xl border border-warning/35 bg-warning-subtle/30 px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground">
                            Inputs needed before calculation
                          </p>
                          <span className="text-xs font-medium text-warning">
                            {calculationReadiness.completeCount}/
                            {calculationReadiness.eligibleCount} ready
                          </span>
                        </div>
                        <ul className="mt-2 grid gap-1.5 text-xs text-foreground-muted sm:grid-cols-2">
                          {calculationReadiness.blockers
                            .slice(0, 6)
                            .map((blocker) => (
                              <li
                                key={blocker.employeeId ?? "none"}
                                className="flex items-start justify-between gap-3 rounded-lg border border-border/70 bg-surface/70 px-2.5 py-2"
                              >
                                <span className="font-medium text-foreground">
                                  {blocker.employeeName || "Payroll setup"}
                                </span>
                                <span className="text-right">
                                  {blocker.missing
                                    .map(
                                      (missing) =>
                                        calculationMissingLabel[missing],
                                    )
                                    .join(" · ")}
                                </span>
                              </li>
                            ))}
                        </ul>
                        {calculationReadiness.blockers.length > 6 && (
                          <p className="mt-2 text-xs text-foreground-subtle">
                            +{calculationReadiness.blockers.length - 6} more
                            employees
                          </p>
                        )}
                      </div>
                    )}
                  {activeWorkflowStage.id === "prepare" &&
                    bulkLockMutation.isError && (
                      <p
                        role="alert"
                        className="mt-3 rounded-xl border border-danger/40 bg-danger-subtle px-3 py-2 text-sm text-danger"
                      >
                        {bulkLockMutation.error instanceof Error
                          ? bulkLockMutation.error.message
                          : "Employees could not be locked."}
                      </p>
                    )}
                  {activeWorkflowStage.id === "prepare" &&
                    bulkLockMutation.data &&
                    (() => {
                      const lockFeedback = formatBulkLockFeedback(
                        bulkLockMutation.data,
                      );
                      return (
                        <div
                          role={
                            lockFeedback.tone === "error" ? "alert" : "status"
                          }
                          className={`mt-3 rounded-xl border px-3 py-2 text-sm ${lockFeedback.tone === "error" ? "border-danger/40 bg-danger-subtle text-danger" : "border-success/40 bg-success-subtle text-success"}`}
                        >
                          <p className="font-medium">{lockFeedback.summary}</p>
                          {bulkLockMutation.data.failed > 0 && (
                            <Button
                              disabled={
                                workflowStateUnavailable ||
                                monthClosed ||
                                !sourceEnabled ||
                                bulkLockMutation.isPending ||
                                bulkUnlockMutation.isPending
                              }
                              onClick={() =>
                                bulkLockMutation.mutate(
                                  bulkLockMutation.data.results
                                    .filter((item) => item.error)
                                    .map((item) => Number(item.userId)),
                                )
                              }
                            >
                              Retry failed employees
                            </Button>
                          )}
                          {lockFeedback.details.length > 0 && (
                            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                              {lockFeedback.details.map((detail) => (
                                <li key={detail}>{detail}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })()}
                  {activeWorkflowStage.id === "pohoda" && (
                    <PayrollPohodaVersions
                        model={{
                          exports: pohodaExports,
                          preview: pohodaPreview,
                          dataQuality: pohodaDataQuality,
                          missingCodeUsers: missingPayrollCodeUsers,
                          outdated: workflowData?.pohodaOutdated === true,
                          requirementsComplete: activeWorkflowStage.requirementsComplete,
                          canGenerate: canGeneratePohoda,
                          loading: pohodaExportsQuery.isLoading,
                          generating: pohodaExportMutation.isPending,
                          downloading: viewPohodaMutation.isPending,
                        }}
                        onGenerate={requestPohodaGeneration}
                        onOpen={setPohodaWorkbookPreviewId}
                        onDownload={(id) => viewPohodaMutation.mutate(id)}
                      />
                  )}
                </div>
                {workflowActionFooter}
              </div>
            </div>
          </Panel>

          {activeWorkflowStage.id === "prepare" &&
          prepareStageTarget
            ? createPortal(
                <PayrollPrepareOverview
                    model={{
                      month,
                      monthClosed,
                      sourceEnabled,
                      hasPayrollAccess,
                      pendingEmployeeId: rowLockMutation.isPending
                        ? rowLockMutation.variables.employeeId
                        : null,
                      uploadPending: uploadPayslipsMutation.isPending,
                      rows: filteredEmployeeOverviewRows,
                      totalCount: employeeOverviewRows.length,
                    }}
                    actions={{
                      onToggleTimesheet: (employeeId) => {
                        const row = employeeOverviewRows.find(
                          (row) => row.user.id === employeeId,
                        );
                        if (row) requestTimesheetToggle(row);
                      },
                      onReplacePayslip: requestPayslipReplacement,
                      onOpenPayslip: openPayslipSheet,
                    }}
                    header={
                      <div
                        id="monthly-employee-overview"
                        className="shrink-0 scroll-mt-6 border-t border-border px-1 pb-2 pt-3"
                      >
                        <h2 ref={workflowHeadingRef} tabIndex={-1} className="sr-only">
                          Employee
                        </h2>
                        <div className="mt-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
                          <label className="block w-full min-w-0 sm:min-w-72 sm:max-w-md sm:flex-1">
                            <span className="mb-1 block text-xs font-medium">
                              Search employees
                            </span>
                            <span className="relative block">
                              <Search
                                size={16}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle"
                                aria-hidden="true"
                              />
                              <input
                                type="search"
                                value={employeeSearch}
                                onChange={(event) => setEmployeeSearch(event.target.value)}
                                placeholder="Name, email or employee code"
                                className="min-h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors duration-200 placeholder:text-foreground-subtle focus:border-accent focus:ring-2 focus:ring-accent/20"
                              />
                            </span>
                          </label>
                          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                            {activeWorkflowStage.id === "prepare" && (
                              <>
                                <ScopedRefreshControl
                                  scope="payroll"
                                  label="Pull TimeSheet"
                                  buttonText="Pull TimeSheet"
                                  busyText="Pulling timesheets…"
                                  disabled={!sourceEnabled}
                                  onDone={() =>
                                    refreshScopedSourceQueries(queryClient, "payroll", month)
                                  }
                                />
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    className="min-h-11"
                                    disabled={
                                      workflowStateUnavailable ||
                                      monthClosed ||
                                      bulkLockMutation.isPending ||
                                      bulkUnlockMutation.isPending ||
                                      rowLockMutation.isPending
                                    }
                                    onClick={() =>
                                      setConfirmation({
                                        title: "Unlock all for " + monthLabel(month) + "?",
                                        description:
                                          "Release all current locks for this month, including employees now excluded from payroll. Historical snapshots and PDFs stay saved. POHODA, balances, approval and closure will need review again.",
                                        confirmLabel: "Unlock all",
                                        requiresReason: true,
                                        tone: "danger",
                                        onConfirm: (reason) =>
                                          bulkUnlockMutation.mutate(reason!),
                                      })
                                    }
                                  >
                                    Unlock all
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={() =>
                                      setConfirmation({
                                        title: `Lock ${readyTimesheetCount} of ${monthlyPayrollUsers.length} employees for ${monthLabel(month)}?`,
                                        description: `${readyTimesheetCount} employee${readyTimesheetCount === 1 ? "" : "s"} ${readyTimesheetCount === 1 ? "has" : "have"} OpenProject evidence that will be refreshed before locking. ${lockedTimesheetCount ? `${lockedTimesheetCount} already locked. ` : ""}${timesheetLockSummary.unavailable ? `${timesheetLockSummary.unavailable} cannot be locked until their OpenProject employee link is available.` : ""}`,
                                        confirmLabel: `Lock ${readyTimesheetCount} employee${readyTimesheetCount === 1 ? "" : "s"}`,
                                        onConfirm: () => bulkLockMutation.mutate(undefined),
                                      })
                                    }
                                    disabled={
                                      monthClosed ||
                                      !sourceEnabled ||
                                      bulkLockMutation.isPending ||
                                      bulkUnlockMutation.isPending ||
                                      rowLockMutation.isPending ||
                                      timesheetStatesLoading ||
                                      workflowStateUnavailable ||
                                      readyTimesheetCount === 0
                                    }
                                    variant="secondary"
                                    size="sm"
                                    className="min-h-11"
                                  >
                                    <LockKeyhole size={16} aria-hidden="true" />
                                    {bulkLockMutation.isPending
                                      ? "Locking…"
                                      : timesheetStatesLoading
                                        ? "Checking locks…"
                                        : "Lock all"}
                                  </Button>
                                </div>
                                {(bulkLockMutation.isPending ||
                                  bulkUnlockMutation.isPending) && (
                                  <p role="status">{bulkProgress || "Processing employees…"}</p>
                                )}
                              </>
                            )}
                            <select
                              aria-label="Filter employees by payroll status"
                              value={employeeStatusFilter}
                              onChange={(event) =>
                                setEmployeeStatusFilter(
                                  event.target.value as typeof employeeStatusFilter,
                                )
                              }
                              className="min-h-11 min-w-0 rounded-lg border border-border bg-surface px-2 text-xs"
                            >
                              <option value="all">All {employeeOverviewRows.length}</option>
                              <option value="attention">Review {attentionEmployeeCount}</option>
                              <option value="complete">Complete {completeEmployeeCount}</option>
                            </select>
                          </div>
                        </div>
                        {activeWorkflowStage.id === "prepare" && !sourceEnabled && (
                          <p role="status" className="mt-2 text-xs text-foreground-muted">
                            {sourceCapabilityQuery.isPending
                              ? "Checking OpenProject connection…"
                              : "Pull and new timesheet locks need an enabled OpenProject connection."}{" "}
                            {sourceCapabilityQuery.isError ? (
                              <button
                                type="button"
                                className="underline"
                                onClick={() => sourceCapabilityQuery.refetch()}
                              >
                                Retry connection check
                              </button>
                            ) : (
                              <Link to="/settings" className="underline">
                                Open connection settings
                              </Link>
                            )}
                          </p>
                        )}
                        {activeWorkflowStage.id === "prepare" && monthClosed && (
                          <p className="mt-2 text-xs text-foreground-muted">
                            Reopen this month before changing its timesheet locks.
                          </p>
                        )}
                      </div>
                    }
                  />,
                prepareStageTarget,
              )
            : null}

          <input
            ref={replacementFileInputRef}
            type="file"
            accept="application/pdf"
            className="sr-only"
            tabIndex={-1}
            disabled={!canUploadPayslips || workflowStateUnavailable || uploadPayslipsMutation.isPending}
            aria-label="Choose replacement payslip PDF"
            onChange={(event) => {
              if (payslipUploadPendingRef.current) {
                event.target.value = "";
                return;
              }
              const files = Array.from(event.target.files ?? []);
              setPayslipFiles(files);
              if (files.length) {
                setSelectedWorkflowStage("payslips");
                setFeedback({
                  tone: "info",
                  message: `${files[0]?.name ?? "Replacement PDF"} is ready to upload. Uploading a valid replacement updates this employee's payslip and requires confirmation and balance recalculation.`,
                });
              }
              event.target.value = "";
            }}
          />

          {activeWorkflowStage.id === "reconcile" && (
            <>
              {calculationStageTarget
                ? createPortal(
                    <PayrollBalanceLedger
                        model={{
                          rows: calculationLedgerRows,
                          totalCount: calculationRows.length,
                          hiddenCount: hiddenZeroValueCalculationCount,
                          hideInactive: hideZeroValueCalculationRows,
                          displayUnit,
                          showPaidLeave: showCalculationPaidLeave,
                          monthClosed,
                          blocking: {
                            count: approvalBlockingCheckCount,
                            employees: approvalBlockingLog,
                            inputsReady: calculationInputsReady,
                          },
                          calculation: {
                            error: balanceCalculationFailure,
                            approvalError: approveBalanceMutation.isError
                              ? payrollLoadErrorMessage(approveBalanceMutation.error)
                              : null,
                            busy: calculateBalanceMutation.isPending,
                            automaticPending: automaticBalanceCalculationPending,
                            manualAvailable: manualRecalculationAvailable,
                            recalculationPending,
                            retryResultId: recalculationRetryResultId,
                            retryUserId: recalculationRetryUserId,
                          },
                          rowRefresh: {
                            busy: calculateBalanceRowMutation.isPending,
                            disabled:
                              monthClosed ||
                              calculateBalanceRowMutation.isPending ||
                              calculateBalanceMutation.isPending ||
                              !latestBalanceRun,
                            pendingEmployeeId: calculateBalanceRowMutation.isPending
                              ? calculateBalanceRowMutation.variables.employeeId
                              : undefined,
                            errorEmployeeId:
                              calculateBalanceRowMutation.isError &&
                              calculateBalanceRowMutation.variables.month === month
                                ? calculateBalanceRowMutation.variables.employeeId
                                : undefined,
                            error: calculateBalanceRowMutation.isError
                              ? payrollLoadErrorMessage(calculateBalanceRowMutation.error)
                              : null,
                          },
                        }}
                        actions={{
                          onResolve: openBalanceResolution,
                          onShowResult: showBalanceResult,
                          onOpenPayslip: openPayslipSheet,
                          onEditOvertime: setOvertimeEditEmployeeId,
                          onRecalculate: requestBalanceCalculation,
                          onRefreshRow: (employeeId) => {
                            if (latestBalanceRun)
                              calculateBalanceRowMutation.mutate({
                                month,
                                employeeId,
                                runId: latestBalanceRun.id,
                                revision: latestBalanceRun.revision,
                              });
                          },
                          onDisplayUnit: setDisplayUnit,
                          onToggleInactive: () =>
                            setHideZeroValueCalculationRows((hidden) => !hidden),
                        }}
                      />,
                    calculationStageTarget,
                  )
                : null}

              {/*
              <Modal
                open={adjustmentEditorOpen}
                onOpenChange={(open) => {
                  setAdjustmentEditorOpen(open);
                  if (!open) {
                    adjustmentRequestRef.current = null;
                    setAdjustmentTarget(null);
                  }
                }}
                title={
                  adjustmentTarget
                    ? `Adjust balance · ${adjustmentTarget.name}`
                    : "Adjust balance"
                }
                description={`Correct the selected employee's balance for ${monthLabel(month)}.`}
                size="md"
                closeLabel="Close manual adjustment"
                footer={
                  <div className="flex w-full items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        adjustmentRequestRef.current = null;
                        setAdjustmentEditorOpen(false);
                        setAdjustmentTarget(null);
                      }}
                      className="cursor-pointer rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground-muted transition-colors hover:bg-elevated hover:text-foreground"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={submitAdjustment}
                      disabled={
                        adjustmentMutation.isPending ||
                        !adjustmentDraft.userId ||
                        !adjustmentDraft.amount ||
                        !auditReasonIsValid(adjustmentDraft.reason)
                      }
                      className="cursor-pointer rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {adjustmentMutation.isPending
                        ? "Saving..."
                        : "Save adjustment"}
                    </button>
                  </div>
                }
              >
                <div className="grid min-w-0 gap-5 pt-5">
                  {adjustmentMutation.isError && (
                    <p className="rounded-lg border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger">
                      {adjustmentMutation.error instanceof Error
                        ? adjustmentMutation.error.message
                        : "Could not save the adjustment."}
                    </p>
                  )}
                  <div className="rounded-xl border border-border bg-surface px-3 py-2.5">
                    <p className="text-xs font-medium text-foreground-muted">
                      Employee
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      {adjustmentTarget?.name || "Selected employee"}
                    </p>
                  </div>
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_7.5rem] gap-3">
                    <label className="grid min-w-0 gap-1.5 text-sm font-medium text-foreground">
                      Balance to adjust
                      <select
                        value={adjustmentDraft.balanceType}
                        onChange={(event) =>
                          setAdjustmentDraft((draft) => ({
                            ...draft,
                            balanceType: event.target
                              .value as AdjustmentDraft["balanceType"],
                          }))
                        }
                        className="min-w-0 w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm font-normal text-foreground"
                      >
                        <option value="overtime_hours">Overtime hours</option>
                        <option value="holiday_days">Holiday days</option>
                      </select>
                    </label>
                    <label className="grid min-w-0 gap-1.5 text-sm font-medium text-foreground">
                      Amount
                      <input
                        value={adjustmentDraft.amount}
                        onChange={(event) =>
                          setAdjustmentDraft((draft) => ({
                            ...draft,
                            amount: event.target.value,
                          }))
                        }
                        inputMode="decimal"
                        placeholder="e.g. -2"
                        className="min-w-0 w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm font-normal text-foreground"
                      />
                    </label>
                  </div>
                  <label className="grid min-w-0 gap-1.5 text-sm font-medium text-foreground">
                    Reason for the change
                    <textarea
                      value={adjustmentDraft.reason}
                      maxLength={AUDIT_REASON_MAX_LENGTH}
                      rows={3}
                      aria-invalid={
                        auditReasonLength(adjustmentDraft.reason) > 0 &&
                        !auditReasonIsValid(adjustmentDraft.reason)
                      }
                      aria-describedby="adjustment-reason-help"
                      onChange={(event) =>
                        setAdjustmentDraft((draft) => ({
                          ...draft,
                          reason: event.target.value,
                        }))
                      }
                      placeholder="Explain why this adjustment is needed"
                      className={`min-w-0 w-full resize-y rounded-lg border bg-surface px-3 py-2.5 text-sm font-normal leading-5 text-foreground outline-none ${auditReasonLength(adjustmentDraft.reason) > 0 && !auditReasonIsValid(adjustmentDraft.reason) ? "border-danger" : "border-border"}`}
                    />
                    <span
                      id="adjustment-reason-help"
                      className="flex justify-between gap-2 text-xs font-normal"
                    >
                      <span
                        className={
                          auditReasonLength(adjustmentDraft.reason) > 0 &&
                          !auditReasonIsValid(adjustmentDraft.reason)
                            ? "text-danger"
                            : "text-foreground-subtle"
                        }
                      >
                        {auditReasonLength(adjustmentDraft.reason) > 0 &&
                        !auditReasonIsValid(adjustmentDraft.reason)
                          ? `${AUDIT_REASON_MIN_LENGTH - auditReasonLength(adjustmentDraft.reason)} more required`
                          : `${AUDIT_REASON_MIN_LENGTH} character minimum`}
                      </span>
                      <span className="tabular-nums text-foreground-subtle">
                        {auditReasonLength(adjustmentDraft.reason)}/
                        {AUDIT_REASON_MAX_LENGTH}
                      </span>
                    </span>
                  </label>
                </div>
              </Modal>
              */}
            </>
          )}
        </>
      )}

      {periodSummaryOnly && (
        <Panel className="payroll-summary-workspace">
          <div
            id="period-summary"
            className="payroll-summary-landmark scroll-mt-6 flex flex-wrap items-start justify-between gap-3"
          >
            <h1 className="sr-only">Period summary</h1>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-foreground-muted">Overtime from</span>
                <MonthYearPicker value={reportFromMonth} onChange={setReportFromMonth} labelPrefix="Overtime from" />
              </label>
              <Button variant="secondary" disabled={overtimeReportMutation.isPending || reportFromMonth > periodThroughMonth} onClick={() => overtimeReportMutation.mutate()}>
                {overtimeReportMutation.isPending ? "Downloading overtime…" : "Download overtime XLSX"}
              </Button>
              {periodSummaryOnly && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground-muted">
                    Display
                  </span>
                  <PayrollUnitToggle
                    value={displayUnit}
                    onChange={setDisplayUnit}
                  />
                </div>
              )}
              <label className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-foreground-muted">
                  Summary through
                </span>
                <MonthYearPicker
                  value={periodThroughMonth}
                  onChange={setPeriodThroughMonth}
                  labelPrefix="Summary through"
                />
              </label>
            </div>
          </div>
          {overtimeReportMutation.isError && (
            <p role="alert" className="mt-2 text-sm text-danger">
              {overtimeReportMutation.error instanceof Error ? overtimeReportMutation.error.message : "Overtime report download failed."}
            </p>
          )}
          {periodSummaryQuery.isError && (
            <p className="mt-2 text-sm text-danger">
              {periodSummaryQuery.error instanceof Error
                ? periodSummaryQuery.error.message
                : "Period summary failed."}
            </p>
          )}
          {visibleYearSummaryRows.length > 0 ? (
            <>
              <div className="mt-3 overflow-hidden rounded-xl border border-border bg-surface md:hidden">
                {visibleYearSummaryRows.map((row) => (
                  <PeriodSummaryMobileCard
                    key={row.userId}
                    row={row}
                    unit={displayUnit}
                    onViewDetails={showEmployeePeriodHistory}
                  />
                ))}
              </div>
              <div
                className="payroll-summary-ledger mt-3 hidden overflow-auto rounded-xl border border-border bg-surface md:block"
                aria-label="Period summary employee ledger"
              >
                <table className="min-w-[1740px] w-full text-left text-sm tabular-nums">
                  <thead className="bg-background text-foreground-muted">
                    <tr className="border-b border-border/70 text-[10px] uppercase tracking-[0.12em] text-foreground-subtle">
                      <th
                        scope="colgroup"
                        colSpan={6}
                        className="px-3 py-1.5 text-left font-semibold"
                      >
                        Work-time evidence
                      </th>
                      <th
                        scope="colgroup"
                        colSpan={showPeriodPaidLeave ? 6 : 5}
                        className="border-l-2 border-border bg-elevated/40 px-3 py-1.5 text-left font-semibold"
                      >
                        Leave &amp; absence
                      </th>
                    </tr>
                    <tr>
                      {[
                        "Employee",
                        "Timesheet total",
                        "Payslip worked",
                        "Contract hours",
                        "Overtime pot",
                      ].map((label, index) => (
                        <th
                          key={label}
                          className={`px-3 py-2 font-medium ${index === 0 ? "sticky left-0 z-10 bg-background" : ""}`}
                        >
                          {label}
                        </th>
                      ))}
                      <ComparisonHeader
                        label="Vacation remaining"
                        left="Calculated"
                      />
                      <ComparisonHeader
                        label="Vacation"
                        className="border-l-2 border-border bg-elevated/40"
                      />
                      <ComparisonHeader label="Doctor (source)" />
                      <ComparisonHeader label="Family care" />
                      <ComparisonHeader label="Illness" />
                      <ComparisonHeader label="Public holidays" />
                      {showPeriodPaidLeave && (
                        <ComparisonHeader label="Paid leave" />
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleYearSummaryRows.map((row) => (
                      <tr
                        key={row.userId}
                        className="border-t border-border text-foreground"
                      >
                        <td className="sticky left-0 z-10 bg-surface px-3 py-1">
                          <button
                            type="button"
                            onClick={() =>
                              showEmployeePeriodHistory(row.userId)
                            }
                            aria-label={`View period details for ${row.employeeName}`}
                            title={`View period details for ${row.employeeName}`}
                            className="group flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-2 text-left transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            <span className="font-medium group-hover:text-accent">
                              {row.employeeName}
                            </span>
                            <Eye
                              size={15}
                              aria-hidden="true"
                              className="shrink-0 text-foreground-subtle group-hover:text-accent"
                            />
                          </button>
                        </td>
                        <PeriodHoursMetricCell
                          rows={row.monthlyRows}
                          valueKey="timesheetHours"
                          unit={displayUnit}
                        />
                        <PeriodHoursMetricCell
                          rows={row.monthlyRows}
                          valueKey="payslipPaidHours"
                          unit={displayUnit}
                        />
                        <PeriodHoursMetricCell
                          rows={row.monthlyRows}
                          valueKey="payslipNominalHours"
                          unit={displayUnit}
                        />
                        <OvertimePotMetricCell row={row} unit={displayUnit} />
                        <ComparisonMetricCell
                          check={row.remainingVacationCrossCheck as CrossCheck}
                          fallbackLeftHours={row.remainingVacationHours}
                          unit={displayUnit}
                          hoursPerDay={
                            [...row.monthlyRows]
                              .reverse()
                              .find(
                                (monthRow) =>
                                  typeof monthRow.hoursPerDay === "number",
                              )?.hoursPerDay ?? null
                          }
                        />
                        <PeriodComparisonMetricCell
                          rows={row.monthlyRows}
                          checkKey="vacation"
                          valueKey="vacationHours"
                          unit={displayUnit}
                          className="border-l-2 border-border bg-elevated/20"
                        />
                        <PeriodComparisonMetricCell
                          rows={row.monthlyRows}
                          checkKey="doctor"
                          valueKey="doctorHours"
                          unit={displayUnit}
                        />
                        <PeriodComparisonMetricCell
                          rows={row.monthlyRows}
                          checkKey="familyCare"
                          valueKey="familyCareHours"
                          unit={displayUnit}
                        />
                        <PeriodComparisonMetricCell
                          rows={row.monthlyRows}
                          checkKey="illness"
                          valueKey="illnessHours"
                          unit={displayUnit}
                        />
                        <PeriodComparisonMetricCell
                          rows={row.monthlyRows}
                          checkKey="publicHoliday"
                          valueKey="publicHolidayHours"
                          unit={displayUnit}
                        />
                        {showPeriodPaidLeave && (
                          <PeriodComparisonMetricCell
                            rows={row.monthlyRows}
                            checkKey="paidLeave"
                            valueKey="paidLeaveHours"
                            unit={displayUnit}
                          />
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : yearSummaryRows.length > 0 && hideZeroValueEmployees ? (
            <p className="mt-3 rounded-xl border border-border bg-surface px-3 py-4 text-center text-sm text-foreground-subtle">
              All {yearSummaryRows.length} employee
              {yearSummaryRows.length === 1 ? " is" : "s are"} hidden because
              every tracked value rounds to zero.
            </p>
          ) : (
            <p className="mt-3 rounded-xl border border-border bg-surface px-3 py-4 text-center text-sm text-foreground-subtle">
              {periodSummaryQuery.isLoading
                ? "Loading period summary..."
                : "No approved payroll results are available for this period yet. Complete approval to add totals; close the month to make its locked payslip available."}
            </p>
          )}
          {yearSummaryRows.length > 0 &&
            (!hideZeroValueEmployees || hiddenZeroValueEmployeeCount > 0) && (
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setHideZeroValueEmployees((hidden) => !hidden)}
                  className="min-h-8 cursor-pointer px-1 text-xs font-medium text-foreground-subtle opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {hideZeroValueEmployees
                    ? `Show ${hiddenZeroValueEmployeeCount} zero-value employee${hiddenZeroValueEmployeeCount === 1 ? "" : "s"}`
                    : "Hide zero-value employees"}
                </button>
              </div>
            )}
        </Panel>
      )}

      {selectedYearEmployee && selectedYearEmployeeTotals && (
        <Modal
          open
          onOpenChange={(open: boolean) => {
            if (!open) {
              setValueCorrectionTarget(null);
              setSelectedYearEmployee(null);
            }
          }}
          title={selectedYearEmployee.employeeName}
          description={`${monthLabel(periodFromMonth)} through ${monthLabel(periodThroughMonth)}`}
          size="full"
          scroll="inside"
          isDismissable={
            !valueCorrectionTarget && !payrollAuditOverlayOpen && !payslipSheet
          }
          isKeyboardDismissDisabled={
            payrollAuditOverlayOpen || Boolean(payslipSheet)
          }
          closeLabel="Close employee period detail"
          className="period-detail-modal"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <PayrollAuditLinks
              compact
              context={{
                month,
                periodThrough: periodThroughMonth,
                employeeId: selectedYearEmployee.userId,
                userId: selectedYearEmployee.userId,
              }}
            />
          </div>
          <PeriodDetailTotals
            employee={selectedYearEmployee}
            totals={selectedYearEmployeeTotals}
            unit={displayUnit}
          />
          <section
            className="mt-3 overflow-hidden rounded-xl border border-border"
            aria-labelledby="monthly-history-title"
          >
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b border-border px-3">
              <h2
                id="monthly-history-title"
                className="text-sm font-semibold text-foreground"
              >
                Monthly history
              </h2>
              {emptyPeriodMonthCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowEmptyPeriodMonths((show) => !show)}
                  aria-expanded={showEmptyPeriodMonths}
                  aria-controls="period-month-history"
                  className="min-h-11 cursor-pointer rounded-md px-2 text-xs text-foreground-muted hover:bg-elevated hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {showEmptyPeriodMonths ? "Hide" : "Show"}{" "}
                  {emptyPeriodMonthCount}{" "}
                  {emptyPeriodMonthCount === 1 ? "month" : "months"} without
                  payroll
                </button>
              )}
            </div>
            <div
              id="period-month-history"
              className="max-h-[72vh] overflow-auto"
            >
              <table
                aria-label="Employee monthly payroll history"
                className="w-full min-w-[1500px] text-left text-sm leading-5 tabular-nums"
              >
                <thead className="sticky top-0 z-10 bg-background text-xs text-foreground-muted shadow-[0_1px_0_var(--color-border)]">
                  <tr>
                    {[
                      "Month",
                      "Timesheet",
                      "Payslip worked",
                      "Contract hours",
                      "Overtime",
                    ].map((label, index) => (
                      <th
                        key={label}
                        className={`px-2 py-2 font-medium whitespace-normal ${index === 0 ? "sticky left-0 z-20 min-w-40 bg-background" : ""}`}
                      >
                        {label}
                      </th>
                    ))}
                    <ComparisonHeader label="Vacation" />
                    <ComparisonHeader
                      label="Vacation remaining"
                      left="Calculated"
                    />
                    <ComparisonHeader label="Doctor (source)" />
                    <ComparisonHeader label="Family care" />
                    <ComparisonHeader label="Illness" />
                    <ComparisonHeader label="Public holidays" />
                    {showSelectedPeriodPaidLeave && (
                      <ComparisonHeader label="Paid leave" />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visiblePeriodMonths.map((row) => (
                    <tr
                      key={row.month}
                      className="border-t border-border text-foreground"
                    >
                      <td className="sticky left-0 z-10 bg-surface px-2 py-1 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {row.closed &&
                          row.payslipAvailable &&
                          row.payslipRecordId ? (
                            <button
                              type="button"
                              onClick={() =>
                                openPayslipSheet(
                                  { id: row.payslipRecordId! },
                                  "read",
                                )
                              }
                              aria-label={`View ${monthLabel(row.month)} payslip for ${selectedYearEmployee.employeeName}`}
                              title={`View ${monthLabel(row.month)} payslip`}
                              className="group inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-1 font-medium text-accent transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                              {shortMonthLabel(row.month)}
                              <Eye
                                size={15}
                                aria-hidden="true"
                                className="shrink-0"
                              />
                            </button>
                          ) : (
                            <span
                              className="font-medium"
                              title={
                                row.closed
                                  ? "No published payslip is available for this month."
                                  : "Close the month to publish its payslip."
                              }
                            >
                              {shortMonthLabel(row.month)}
                            </span>
                          )}
                          <WorkloadBadge
                            percent={row.payrollWorkloadPercent ?? null}
                            student={
                              row.payrollContractType === "student_agreement"
                            }
                          />
                          {row.approved && (
                            <span className="rounded-full bg-success-subtle px-2 py-0.5 text-xs text-success">
                              {row.closed ? "Closed" : "Approved"}
                            </span>
                          )}
                        </div>
                      </td>
                      {!row.hasResult ? (
                        <td
                          colSpan={showSelectedPeriodPaidLeave ? 11 : 10}
                          className="px-2 py-3 text-foreground-subtle"
                        >
                          No approved payroll result
                        </td>
                      ) : (
                        <>
                          <ValueCorrectionMetricCell
                            row={row}
                            fieldKey="timesheet.total"
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionMetricCell
                            row={row}
                            fieldKey="payslip.paid"
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <MetricCell>
                            {formatDuration(
                              row.payslipNominalHours,
                              displayUnit,
                              row.hoursPerDay,
                            )}
                          </MetricCell>
                          <ValueCorrectionMetricCell
                            row={row}
                            fieldKey={"overtime.calculated" as const}
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            check={row.crossChecks?.["overtime"] as CrossCheck}
                            title={
                              mismatchTooltip(
                                row.crossChecks?.["overtime"],
                                displayUnit,
                                row.hoursPerDay,
                              ) as string
                            }
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"vacation.timesheet" as const}
                            rightKey={"vacation.payslip" as const}
                            check={row.crossChecks?.["vacation"] as CrossCheck}
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"vacation_remaining.calculated" as const}
                            rightKey={"vacation_remaining.payslip" as const}
                            check={
                              row.crossChecks?.[
                                "remainingVacation"
                              ] as CrossCheck
                            }
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"doctor.timesheet" as const}
                            rightKey={"doctor.payslip" as const}
                            check={row.crossChecks?.["doctor"] as CrossCheck}
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"family_care.timesheet" as const}
                            rightKey={"family_care.payslip" as const}
                            check={
                              row.crossChecks?.["familyCare"] as CrossCheck
                            }
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"illness.timesheet" as const}
                            rightKey={"illness.payslip" as const}
                            check={row.crossChecks?.["illness"] as CrossCheck}
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          <ValueCorrectionComparisonMetricCell
                            row={row}
                            leftKey={"public_holiday.timesheet" as const}
                            rightKey={"public_holiday.payslip" as const}
                            check={
                              row.crossChecks?.["publicHoliday"] as CrossCheck
                            }
                            unit={displayUnit}
                            hoursPerDay={row.hoursPerDay}
                            onOpen={(fieldKey, trigger) =>
                              openValueCorrection(row, fieldKey, trigger)
                            }
                          />
                          {showSelectedPeriodPaidLeave && (
                            <ValueCorrectionComparisonMetricCell
                              row={row}
                              leftKey={"paid_leave.timesheet" as const}
                              rightKey={"paid_leave.payslip" as const}
                              check={
                                row.crossChecks?.["paidLeave"] as CrossCheck
                              }
                              unit={displayUnit}
                              hoursPerDay={row.hoursPerDay}
                              onOpen={(fieldKey, trigger) =>
                                openValueCorrection(row, fieldKey, trigger)
                              }
                            />
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                  {visiblePeriodMonths.length === 0 && (
                    <tr>
                      <td
                        colSpan={showSelectedPeriodPaidLeave ? 12 : 11}
                        className="px-3 py-6 text-center text-foreground-subtle"
                      >
                        No approved payroll results or published payslips in
                        this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </Modal>
      )}

      {valueCorrectionTarget && (
        <ValueCorrectionInspector
          target={valueCorrectionTarget}
          onClose={() => setValueCorrectionTarget(null)}
        />
      )}

      {selectedYearEmployee && employeeAuditOpen && (
        <Modal
          open
          onOpenChange={(open: boolean) => {
            if (!open) setEmployeeAuditOpen(false);
          }}
          title="Manual adjustments"
          description={`${selectedYearEmployee.employeeName} · ${shortMonthLabel(periodFromMonth)} – ${shortMonthLabel(periodThroughMonth)}`}
          size="xl"
          scroll="inside"
          closeLabel="Close employee audit"
          className="employee-audit-modal"
        >
          <ol className="mx-auto max-h-[min(65vh,40rem)] divide-y divide-border overflow-y-auto rounded-xl border border-border bg-surface">
            {employeeAuditQuery.isLoading && (
              <p className="px-4 py-6 text-sm text-foreground-subtle">
                Loading audit events...
              </p>
            )}
            {employeeAuditQuery.isError && (
              <p className="px-4 py-6 text-sm text-danger">
                {employeeAuditQuery.error instanceof Error
                  ? employeeAuditQuery.error.message
                  : "Audit failed."}
              </p>
            )}
            {employeeAuditEvents.map((event, index) => (
              <EmployeeAuditTimelineItem
                key={event.id}
                event={event}
                last={index === employeeAuditEvents.length - 1}
              />
            ))}
            {!employeeAuditQuery.isLoading &&
              employeeAuditEvents.length === 0 && (
                <p className="px-4 py-6 text-sm text-foreground-subtle">
                  No manual adjustments for this employee and period.
                </p>
              )}
          </ol>
        </Modal>
      )}

      {monthChangeLogPreviewId && (
        <Modal
          open
          onOpenChange={(open: boolean) => {
            if (!open) setMonthChangeLogPreviewId(null);
          }}
          title="Month change log"
          description={
            monthChangeLogPreviewQuery.data
              ? monthChangeLogPreviewQuery.data.fileName +
                " · sealed published-month report"
              : "Opening the published manual-change report"
          }
          size="xl"
          scroll="inside"
          closeLabel="Close month change log"
          footer={
            <div className="flex w-full items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setMonthChangeLogPreviewId(null)}
                className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-foreground-muted transition-colors hover:bg-elevated hover:text-foreground"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() =>
                  viewMonthChangeLogMutation.mutate(monthChangeLogPreviewId)
                }
                disabled={
                  viewMonthChangeLogMutation.isPending ||
                  monthChangeLogPreviewQuery.isLoading
                }
                className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileText size={16} aria-hidden="true" />
                {viewMonthChangeLogMutation.isPending
                  ? "Downloading…"
                  : "Download PDF"}
              </button>
            </div>
          }
        >
          <div className="pt-5">
            {monthChangeLogPreviewQuery.isLoading && (
              <div
                aria-label="Loading month change log"
                className="flex min-h-[55vh] items-center justify-center rounded-xl border border-border bg-surface text-sm text-foreground-muted"
              >
                Loading the sealed report…
              </div>
            )}
            {monthChangeLogPreviewQuery.isError && (
              <p
                role="alert"
                className="rounded-xl border border-danger/30 bg-danger-subtle px-4 py-3 text-sm text-danger"
              >
                The month change log could not be loaded. Use Download to try
                the file again.
              </p>
            )}
            {monthChangeLogPreviewUrl && (
              <iframe
                title="Month change log PDF"
                src={monthChangeLogPreviewUrl}
                className="h-[min(70vh,52rem)] w-full rounded-xl border border-border bg-white"
              />
            )}
          </div>
        </Modal>
      )}

      {pohodaWorkbookPreviewId && (
        <Modal
          open
          onOpenChange={(open: boolean) => {
            if (!open) setPohodaWorkbookPreviewId(null);
          }}
          title="Pohoda input"
          description={
            pohodaWorkbookPreviewQuery.data
              ? `${pohodaWorkbookPreviewQuery.data.file.fileName} · saved payroll export`
              : "Opening the saved Pohoda workbook"
          }
          size="xl"
          scroll="inside"
          closeLabel="Close Pohoda input"
          className="pohoda-workbook-modal"
          footer={
            <div className="flex w-full items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setPohodaWorkbookPreviewId(null)}
                className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-foreground-muted transition-colors hover:bg-elevated hover:text-foreground"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() =>
                  viewPohodaMutation.mutate(pohodaWorkbookPreviewId)
                }
                disabled={
                  viewPohodaMutation.isPending ||
                  pohodaWorkbookPreviewQuery.isLoading
                }
                className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileSpreadsheet size={16} aria-hidden="true" />
                {viewPohodaMutation.isPending
                  ? "Downloading…"
                  : pohodaWorkbookPreviewQuery.isLoading
                    ? "Loading workbook…"
                    : "Download .xlsx"}
              </button>
            </div>
          }
        >
          <div className="grid gap-4 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent/25 bg-accent-subtle px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Saved workbook
                </p>
                <p className="mt-0.5 text-xs text-foreground-muted">
                  {pohodaWorkbookPreviewQuery.data?.file.fileName ??
                    "Reading the generated Pohoda .xlsx file directly in your browser."}
                </p>
              </div>
              <span className="rounded-full border border-accent/25 bg-surface px-2.5 py-1 text-xs font-medium text-accent">
                {monthLabel(month)}
              </span>
            </div>
            {pohodaWorkbookPreviewQuery.isLoading && (
              <div
                aria-label="Loading workbook preview"
                className="overflow-hidden rounded-xl border border-border bg-surface"
              >
                <div className="h-11 animate-pulse border-b border-border bg-elevated" />
                <div className="grid gap-px bg-border p-px sm:grid-cols-3">
                  {Array.from({ length: 9 }, (_value, index) => (
                    <div
                      key={index}
                      className="h-11 animate-pulse bg-background"
                    />
                  ))}
                </div>
              </div>
            )}
            {pohodaWorkbookPreviewQuery.isError && (
              <p
                role="alert"
                className="rounded-xl border border-danger/30 bg-danger-subtle px-4 py-3 text-sm text-danger"
              >
                The saved workbook could not be loaded. Use Download to try the
                file again.
              </p>
            )}
            {pohodaWorkbookPreviewQuery.data?.previewError && (
              <p
                role="alert"
                className="rounded-xl border border-danger/30 bg-danger-subtle px-4 py-3 text-sm text-danger"
              >
                {pohodaWorkbookPreviewQuery.data.previewError} You can still
                download the original file.
              </p>
            )}
            {pohodaWorkbookPreviewQuery.data?.preview && (
              <PohodaWorkbookPreviewTable
                preview={pohodaWorkbookPreviewQuery.data.preview}
              />
            )}
          </div>
        </Modal>
      )}

      {balanceResolutionResult && !payslipSheet && (
        <BalanceResolutionModal
          key={balanceResolutionResult.id}
          employee={balanceResolutionResult}
          actorName={
            authQuery.data?.user?.displayName?.trim().split(/\s+/)[0] ||
            authQuery.data?.user?.username ||
            "You"
          }
          monthLabel={monthLabel(month)}
          monthClosed={monthClosed}
          timesheetLocked={Boolean(balanceResolutionTimesheet?.locked)}
          busy={rowLockMutation.isPending}
          displayUnit={displayUnit}
          initialCheckKey={balanceResolutionCheckKey}
          onClose={() => {
            setBalanceResolutionEmployeeId(null);
            setBalanceResolutionCheckKey(null);
          }}
          onOpenPayslip={() => {
            if (!balanceResolutionResult.payslipRecordId) return;
            openPayslipSheet(
              { id: balanceResolutionResult.payslipRecordId },
              "read",
            );
          }}
          onCorrectPayslipWorked={() => {
            setValueCorrectionTarget({
              userId: balanceResolutionResult.userId,
              employeeName: balanceResolutionResult.employeeName,
              month,
              periodThrough: month,
              fieldKey: "payslip.paid",
              displayUnit,
              hoursPerDay: balanceResolutionResult.hoursPerDay,
              trigger: balanceResolutionTriggerRef.current,
            });
            setBalanceResolutionEmployeeId(null);
            setBalanceResolutionCheckKey(null);
          }}
          onDownloadTimesheet={async () => {
            if (!balanceResolutionResult.timesheetVersionId) return;
            await api.downloadOpenProjectTimesheetPdf(
              balanceResolutionResult.timesheetVersionId,
            );
          }}
          onToggleTimesheet={async (action) => {
            await rowLockMutation.mutateAsync({
              employeeId: balanceResolutionResult.userId,
              employeeName: balanceResolutionResult.employeeName,
              action,
            });
          }}
          onReopen={async (reason) => {
            await api.reopenOpenProjectPayrollMonth(month, {
              fromStage: "prepare",
              reason,
              confirmed: true,
            });
            await refreshMonthlyWorkflow();
            setFeedback({
              tone: "info",
              message: `${monthLabel(month)} was reopened for ${balanceResolutionResult.employeeName}'s source correction.`,
            });
            setBalanceResolutionEmployeeId(null);
          }}
          onAccept={async (checkKey, resolutionAction, reason) => {
            const payload: Parameters<
              typeof api.acceptOpenProjectBalanceException
            >[0] & {
              resolutionAction: "confirm_overtime" | "accept_exception";
            } = {
              requestId: crypto.randomUUID(),
              resultId: balanceResolutionResult.id,
              expectedRevision: balanceResolutionResult.revision,
              checkKey,
              resolutionAction,
              reason,
            };
            const data = await api.acceptOpenProjectBalanceException({
              ...payload,
            });
            queryClient.setQueryData(["openproject", "balances", month], data);
            await refreshMonthlyWorkflow();
          }}
          onPreviewAdjust={(checkKey, inputAmount, inputUnit) => {
            const balanceType =
              checkKey === "remainingVacation"
                ? "holiday_days"
                : "overtime_hours";
            return api.previewOpenProjectBalanceAdjustment({
              userId: balanceResolutionResult.userId,
              balanceType,
              inputAmount,
              inputUnit,
              effectiveMonth: month,
            });
          }}
          onAdjust={async (
            checkKey,
            inputAmount,
            inputUnit,
            reason,
            preview,
          ) => {
            const balanceType =
              checkKey === "remainingVacation"
                ? "holiday_days"
                : "overtime_hours";
            if (
              preview.balanceType !== balanceType ||
              preview.inputAmount !== inputAmount ||
              preview.inputUnit !== inputUnit
            ) {
              throw new Error(
                "The adjustment preview no longer matches this input. Preview it again.",
              );
            }
            const data = await api.createOpenProjectBalanceAdjustment({
              requestId: crypto.randomUUID(),
              userId: balanceResolutionResult.userId,
              balanceType,
              adjustmentType: "manual",
              inputAmount,
              inputUnit,
              effectiveMonth: month,
              expectedRevision: preview.revision,
              conversionBasis: preview.conversionBasis,
              reason,
            });
            queryClient.setQueryData(["openproject", "balances", month], data);
            await refreshMonthlyWorkflow();
          }}
        />
      )}

      {overtimeEditResult && (
        <OvertimePotDialog
          key={`${month}:${overtimeEditResult.userId}`}
          employeeId={overtimeEditResult.userId}
          employeeName={overtimeEditResult.employeeName}
          currentHours={overtimeEditResult.closingOvertimeHours}
          month={month}
          onClose={() => setOvertimeEditEmployeeId(null)}
          onSaved={refreshMonthlyWorkflow}
        />
      )}
      {uploadRemovalRecord && (
        <Modal
          open
          title="Remove this payslip?"
          description={`${uploadRemovalRecord.sourceFileName} · ${uploadRemovalRecord.employee?.displayName || uploadRemovalRecord.parsedEmployeeName || "Not mapped"}. History is preserved; affected payroll results must be recalculated.`}
          size="sm"
          isDismissable={!deletePayslipMutation.isPending}
          onOpenChange={(open: boolean) => {
            if (!open) setUploadRemovalRecord(null);
          }}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setUploadRemovalRecord(null)}
                disabled={deletePayslipMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deletePayslipMutation.isPending}
                disabled={
                  !auditReasonIsValid(uploadRemovalReason) ||
                  deletePayslipMutation.isPending
                }
                onClick={() =>
                  deletePayslipMutation.mutate(
                    {
                      record: uploadRemovalRecord,
                      reason: trimmedAuditReason(uploadRemovalReason),
                    },
                    { onSuccess: () => setUploadRemovalRecord(null) },
                  )
                }
              >
                Remove payslip
              </Button>
            </div>
          }
        >
          <label className="grid gap-2 text-sm">
            Reason (at least 15 characters)
            <textarea
              value={uploadRemovalReason}
              onChange={(event) => setUploadRemovalReason(event.target.value)}
              maxLength={500}
              className="rounded-lg border border-border bg-background p-3"
            />
          </label>
          {deletePayslipMutation.error ? (
            <p role="alert" className="text-danger">
              {deletePayslipMutation.error.message}
            </p>
          ) : null}
        </Modal>
      )}

      {payslipSheet && (
        <PayslipSheet
          recordId={payslipSheet.recordId}
          mode={monthClosed ? "read" : payslipSheet.mode}
          onClose={() => setPayslipSheet(null)}
          onUpdated={refreshMonthlyWorkflow}
          onReplace={
            !monthClosed && !uploadPayslipsMutation.isPending && payslipSheet.mode === "edit"
              ? () => {
                  const record = payslipRecords.find(
                    (candidate) => candidate.id === payslipSheet.recordId,
                  );
                  setPayslipSheet(null);
                  requestPayslipReplacement(
                    record?.employee?.displayName ||
                      record?.parsedEmployeeName ||
                      "this employee",
                  );
                }
              : undefined
          }
        />
      )}
      {reopenStage && (
        <Modal
          open
          onOpenChange={(open: boolean) => {
            if (!open) closeReopenDialog();
          }}
          title="Reopen payroll from this stage?"
          description={`${monthLabel(month)} will resume at ${reopenStageTitle ?? "the selected stage"}. Audit history and prior files remain preserved.`}
          size="sm"
          isDismissable={!reopenMonthMutation.isPending}
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={closeReopenDialog}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                size="lg"
                loading={reopenMonthMutation.isPending}
                onClick={() =>
                  reopenMonthMutation.mutate({
                    stage: reopenStage,
                    reason: reopenReason.trim(),
                  })
                }
                disabled={
                  reopenReason.trim().length < 15 ||
                  reopenMonthMutation.isPending
                }
              >
                Reopen and invalidate later stages
              </Button>
            </div>
          }
        >
          <div className="mb-4 rounded-lg border border-warning/35 bg-warning-subtle px-3 py-3 text-sm text-warning">
            <p className="font-semibold">
              The following stages will need to be completed again:
            </p>
            <p className="mt-1 leading-5">
              {invalidatedStageTitles.length
                ? invalidatedStageTitles.join(", ")
                : "No later stages"}
              .
            </p>
          </div>
          <label className="grid gap-2 text-sm font-medium text-foreground">
            Reason{" "}
            <span className="font-normal text-foreground-subtle">
              Required for the audit trail
            </span>
            <textarea
              autoFocus
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Explain why the completed input must change (at least 15 characters)"
              className="resize-none rounded-lg border border-border bg-elevated px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </label>
          <p className="mt-2 text-xs text-foreground-subtle">
            {reopenReason.trim().length}/15 minimum characters
          </p>
          {reopenMonthMutation.isError && (
            <p
              role="alert"
              className="mt-3 border border-danger/40 bg-danger-subtle p-3 text-sm text-danger"
            >
              {reopenMonthMutation.error instanceof Error
                ? reopenMonthMutation.error.message
                : "Payroll could not be reopened."}
            </p>
          )}
        </Modal>
      )}
      {confirmation && (
        <ConfirmDialog
          confirmation={confirmation}
          onClose={() => setConfirmation(null)}
        />
      )}
    </div>
  );
};

export const PayrollPeriodSummaryPage = () => (
  <PayrollAdminPage view="period-summary" />
);
